import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import {
  Activity, BarChart3, LineChart, Search, Settings, HelpCircle,
  Bell, ChevronDown, ChevronRight, RefreshCw,
} from "lucide-react";
import {
  RadarChart as ReRadarChart,
  PolarGrid, PolarAngleAxis, Radar,
  PieChart, Pie, Cell, ResponsiveContainer,
  AreaChart, Area, XAxis, YAxis, Tooltip,
} from "recharts";

/* ──────────────────────────────────────────────────────────────────
 * MARKET INTELLIGENCE — /trading
 * Screenshot-accurate rebuild for the owner. Dark navy + cyan palette,
 * left icon nav, animated "AI Filtering Matrix" centrepiece, sparklines,
 * radar charts, risk matrix, sector heatmap, smart watchlists.
 *
 * Live data wires through /api/trading/overview (Alpha Vantage proxy
 * with Mongo cache). When AV quota is exhausted, the page degrades
 * gracefully to placeholder values that match the mockup.
 * ────────────────────────────────────────────────────────────────── */

/* ───── Theme tokens ───── */
const T = {
  bg:        "#0a1628",
  bg2:       "#0f1e3d",
  panel:     "rgba(15, 30, 61, 0.6)",
  border:    "rgba(34, 211, 238, 0.18)",
  borderHi:  "rgba(34, 211, 238, 0.45)",
  cyan:      "#22d3ee",
  cyanHi:    "#06b6d4",
  green:     "#10b981",
  greenSoft: "rgba(16, 185, 129, 0.22)",
  red:       "#ef4444",
  redSoft:   "rgba(239, 68, 68, 0.22)",
  amber:     "#f59e0b",
  amberSoft: "rgba(245, 158, 11, 0.22)",
  text:      "#e5e7eb",
  textDim:   "#94a3b8",
};

/* ───── Placeholder data (matches screenshot copy) ───── */
// Fallback rows shown when the live backend feed returns 0 movers
// (Alpha Vantage throttled / Capital quiet). Locked to Capital
// instruments so the matrix demo never shows symbols the owner can't
// actually trade. Updated Feb 2026.
const FALLBACK_BUY = [
  { symbol: "GOLD",     price: 2065.30, change_pct:  0.45, rationale: "Safe-haven flow"     },
  { symbol: "NVDA",     price:   33.55, change_pct:  5.60, rationale: "AI Sector Strength"  },
  { symbol: "MSTR",     price:  123.88, change_pct: 20.30, rationale: "BTC treasury surge"  },
  { symbol: "PLATINUM", price:  972.00, change_pct:  3.20, rationale: "Industrial demand"   },
  { symbol: "MSFT",     price:  410.00, change_pct:  1.40, rationale: "Cloud earnings beat" },
];

const TREND_INDICES = [
  { name: "S&P 500", change: 25,   pct:  6.2 },
  { name: "NASDAQ",  change: -350, pct: -3.2 },
  { name: "NASDAQ",  change: 75,   pct:  3.5 },
  { name: "S&P 1000",change: -226, pct: -2.26 },
];

const SECTOR_GRID = [
  ["ABC", "NBD", "JLD", "DBN", "QST", "ICR"],
  ["NHD", "OTS", "VWM", "ASQ", "AGL", "BP"],
  ["WY",  "XJL", "NBC", "TJN", "TOL", "IST"],
];

// Owner's Capital.com-supported watchlist (the 36-symbol roster locked
// in the auto-trader). Shown when the live `/api/trading/capital_watchlist`
// hasn't populated yet so the UI never displays AAPL/TSLA placeholders.
const WATCHLIST = [
  { sym: "GOLD",      group: "Commodity Strength", pct: +0.45, sub: "Capital watchlist · safe-haven flow"     },
  { sym: "NVDA",      group: "AI Sector Strength", pct: +0.29, sub: "Capital watchlist · AI mover"            },
  { sym: "MSTR",      group: "BTC Treasury",       pct: +0.08, sub: "Capital watchlist · Bitcoin proxy"       },
  { sym: "BTCUSD",    group: "Crypto Strength",    pct: +1.40, sub: "Capital watchlist · digital asset"       },
  { sym: "PLATINUM",  group: "Commodity Strength", pct: +3.31, sub: "Capital watchlist · industrial metal"    },
  { sym: "OIL_CRUDE", group: "Commodity Weakness", pct: -3.86, sub: "Capital watchlist · energy"              },
  { sym: "NATGAS",    group: "Commodity Weakness", pct: -2.58, sub: "Capital watchlist · energy"              },
  { sym: "META",      group: "Mega Cap",           pct: -0.82, sub: "Capital watchlist · social"              },
  { sym: "MSFT",      group: "Mega Cap",           pct: +0.04, sub: "Capital watchlist · cloud"               },
  { sym: "MARA",      group: "BTC Miners",         pct: +2.10, sub: "Capital watchlist · BTC mining"          },
  { sym: "RIOT",      group: "BTC Miners",         pct: +1.85, sub: "Capital watchlist · BTC mining"          },
];

/* ───── Utility: deterministic seeded RNG (so charts don't reshuffle) ───── */
function seedRng(seed) {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return (s & 0xfffffff) / 0xfffffff;
  };
}
function symSeed(sym) {
  return (sym || "").split("").reduce((a, c) => a + c.charCodeAt(0), 17) | 0;
}

/* ───── Panel wrapper ───── */
function Panel({ title, right, children, className = "", testid }) {
  return (
    <div
      className={`relative rounded-md ${className}`}
      style={{
        background: T.panel,
        border: `1px solid ${T.border}`,
        backdropFilter: "blur(8px)",
      }}
      data-testid={testid}
    >
      {(title || right) && (
        <div className="flex items-center justify-between px-3 pt-2.5 pb-1.5">
          <div className="text-[12px] font-semibold tracking-wide" style={{ color: T.text }}>{title}</div>
          {right || <button className="opacity-50 hover:opacity-100"><ChevronDown size={12} /></button>}
        </div>
      )}
      <div className="px-3 pb-3">{children}</div>
    </div>
  );
}

/* ───── Sparkline (mini line chart for watchlist + buy opps) ───── */
function Sparkline({ seed = 1, up = true, width = 80, height = 28 }) {
  const r = seedRng(seed);
  const pts = Array.from({ length: 20 }, (_, i) => {
    const x = (i / 19) * width;
    const drift = up ? (i / 19) * 12 : -(i / 19) * 8;
    const y = height / 2 + (r() - 0.5) * 14 - drift;
    return [x, Math.max(2, Math.min(height - 2, y))];
  });
  const d = "M " + pts.map(p => p.join(",")).join(" L ");
  const stroke = up ? T.green : T.red;
  const fill = up ? "rgba(16,185,129,0.18)" : "rgba(239,68,68,0.18)";
  const area = d + ` L ${width},${height} L 0,${height} Z`;
  return (
    <svg width={width} height={height} className="overflow-visible">
      <path d={area} fill={fill} stroke="none"/>
      <path d={d} fill="none" stroke={stroke} strokeWidth="1.5"/>
    </svg>
  );
}

/* ───── Candlestick chart for Market Trends ───── */
function CandleChart({ seed, name, change, pct, height = 70 }) {
  const r = seedRng(seed);
  const N = 18;
  const candles = Array.from({ length: N }, () => {
    const o = 30 + r() * 50;
    const c = o + (r() - 0.5) * 35;
    const h = Math.max(o, c) + r() * 8;
    const l = Math.min(o, c) - r() * 8;
    return { o, c, h, l, up: c >= o };
  });
  const min = Math.min(...candles.map(k => k.l));
  const max = Math.max(...candles.map(k => k.h));
  const scale = (v) => height - ((v - min) / (max - min)) * (height - 6) - 3;
  const colW = 100 / N;
  const up = (pct ?? 0) >= 0;
  return (
    <div className="rounded-sm" style={{ border: `1px solid ${T.border}`, padding: "8px 8px 4px" }}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px]" style={{ color: T.text }}>{name}</span>
        <span className="text-[11px] font-semibold inline-flex items-center gap-0.5" style={{ color: up ? T.green : T.red }}>
          {up ? "+" : ""}{change}
          <ChevronRight size={10} style={{ transform: up ? "rotate(-90deg)" : "rotate(90deg)" }}/>
        </span>
      </div>
      <svg viewBox={`0 0 100 ${height}`} preserveAspectRatio="none" style={{ width: "100%", height }}>
        {candles.map((k, i) => {
          const cx = i * colW + colW / 2;
          const yh = scale(k.h), yl = scale(k.l);
          const yo = scale(k.o), yc = scale(k.c);
          const yTop = Math.min(yo, yc);
          const bodyH = Math.max(1, Math.abs(yc - yo));
          const col = k.up ? T.green : T.red;
          return (
            <g key={i}>
              <line x1={cx} x2={cx} y1={yh} y2={yl} stroke={col} strokeWidth="0.4"/>
              <rect x={cx - colW * 0.35} width={colW * 0.7} y={yTop} height={bodyH} fill={col} opacity="0.9"/>
            </g>
          );
        })}
      </svg>
      <div className="flex justify-between text-[8px] mt-1" style={{ color: T.textDim }}>
        <span>13:00</span><span>14:00</span><span>15:00</span><span>16:00</span><span>17:00</span>
      </div>
    </div>
  );
}

/* ───── Radar chart (pentagon) ───── */
function RadarChart({ seed = 7, labels = ["A","B","C","D","E"], title }) {
  const r = seedRng(seed);
  const cx = 60, cy = 60, R = 42;
  const N = labels.length;
  const axisPts = (radius) => labels.map((_, i) => {
    const a = (Math.PI * 2 * i) / N - Math.PI / 2;
    return [cx + Math.cos(a) * radius, cy + Math.sin(a) * radius];
  });
  const data = labels.map(() => 0.35 + r() * 0.6);
  const pts = data.map((v, i) => {
    const a = (Math.PI * 2 * i) / N - Math.PI / 2;
    return [cx + Math.cos(a) * R * v, cy + Math.sin(a) * R * v];
  });
  const polyAll = (pts2) => pts2.map(p => p.join(",")).join(" ");
  return (
    <div className="flex flex-col items-center">
      <svg viewBox="0 0 120 130" width="100%" style={{ maxWidth: 140 }}>
        {/* concentric rings */}
        {[1, 0.66, 0.33].map((s, i) => (
          <polygon key={i} points={polyAll(axisPts(R * s))} fill="none" stroke={T.border} strokeWidth="0.5"/>
        ))}
        {/* axes */}
        {axisPts(R).map((p, i) => (
          <line key={i} x1={cx} y1={cy} x2={p[0]} y2={p[1]} stroke={T.border} strokeWidth="0.5"/>
        ))}
        {/* data polygon */}
        <polygon points={polyAll(pts)} fill="rgba(34,211,238,0.25)" stroke={T.cyan} strokeWidth="1.2"/>
        {pts.map((p, i) => <circle key={i} cx={p[0]} cy={p[1]} r="1.6" fill={T.cyan}/>)}
        {/* labels */}
        {labels.map((lab, i) => {
          const a = (Math.PI * 2 * i) / N - Math.PI / 2;
          const x = cx + Math.cos(a) * (R + 10);
          const y = cy + Math.sin(a) * (R + 10) + 2;
          return <text key={i} x={x} y={y} fontSize="6" textAnchor="middle" fill={T.textDim}>{lab}</text>;
        })}
      </svg>
      {title && <div className="text-[9px] mt-0.5" style={{ color: T.textDim }}>{title}</div>}
    </div>
  );
}

/* ───── Risk gauge (semicircle dial) ───── */
function RiskGauge({ value = 0.55, size = 110 }) {
  const angle = -90 + value * 180;
  const cx = size / 2, cy = size * 0.62;
  const r = size * 0.38;
  const needleX = cx + Math.cos((angle * Math.PI) / 180) * r * 0.85;
  const needleY = cy + Math.sin((angle * Math.PI) / 180) * r * 0.85;
  return (
    <svg width={size} height={size * 0.75} viewBox={`0 0 ${size} ${size * 0.75}`}>
      <defs>
        <linearGradient id="rg-grad" x1="0%" x2="100%">
          <stop offset="0%" stopColor={T.green}/>
          <stop offset="50%" stopColor={T.amber}/>
          <stop offset="100%" stopColor={T.red}/>
        </linearGradient>
      </defs>
      <path
        d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
        fill="none" stroke="url(#rg-grad)" strokeWidth="9" strokeLinecap="round"
      />
      <line x1={cx} y1={cy} x2={needleX} y2={needleY} stroke="#fff" strokeWidth="2"/>
      <circle cx={cx} cy={cy} r="3" fill="#fff"/>
      <text x={cx - r} y={cy + 14} fontSize="8" fill={T.textDim}>Low</text>
      <text x={cx} y={cy + 14} fontSize="8" textAnchor="middle" fill={T.text}>RISK</text>
      <text x={cx + r} y={cy + 14} fontSize="8" textAnchor="end" fill={T.textDim}>High</text>
    </svg>
  );
}

/* ───── Left nav ─────
   Owner brief Feb 2026: "Non of them button on the left … do anything that
   setting should pop up all this so I can change everything myself".
   Wired up: Settings ⚙ opens the AUTO-TRADER SETTINGS panel where the owner
   can change per-trade £, daily cap, max open positions, stop/take pips,
   whitelist, and the ARM/DISARM master switch. Other icons are stubs that
   scroll to / focus the relevant section of the page.
*/
function LeftNav({ onAction, activeKey = "chart" }) {
  const items = [
    { icon: Search,    key: "search",   title: "Search (focus watchlist)" },
    { icon: LineChart, key: "chart",    title: "Charts (matrix view)" },
    { icon: BarChart3, key: "bars",     title: "Sector heatmap" },
    { icon: Activity,  key: "activity", title: "Auto-trade activity log" },
    { icon: HelpCircle,key: "help",     title: "How auto-trader works" },
    { icon: Settings,  key: "settings", title: "Auto-trader settings" },
  ];
  return (
    <div
      className="flex flex-col items-center py-3 gap-3 flex-shrink-0"
      style={{ width: 44, background: "rgba(7,17,33,0.85)", borderRight: `1px solid ${T.border}` }}
      data-testid="trading-leftnav"
    >
      <div
        className="w-7 h-7 rounded-sm flex items-center justify-center"
        style={{ background: T.cyan, color: T.bg, fontWeight: 700, fontSize: 14 }}
      >▲</div>
      {items.map((it) => {
        const Icon = it.icon;
        const active = it.key === activeKey;
        return (
          <button
            key={it.key}
            type="button"
            onClick={() => onAction?.(it.key)}
            title={it.title}
            className="w-8 h-8 rounded-sm flex items-center justify-center hover:opacity-100 transition-all cursor-pointer"
            style={{
              color: active ? T.cyan : T.textDim,
              background: active ? "rgba(34,211,238,0.12)" : "transparent",
              opacity: active ? 1 : 0.7,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.color = T.cyan; }}
            onMouseLeave={(e) => { if (!active) { e.currentTarget.style.opacity = "0.7"; e.currentTarget.style.color = T.textDim; } }}
            data-testid={`trading-nav-${it.key}`}
          >
            <Icon size={16}/>
          </button>
        );
      })}
    </div>
  );
}

/* ───── Auto-trader settings modal ─────
   Reads /api/trading/auto_settings, lets the owner edit per-trade £,
   daily cap, max open positions, stop/take pips, min confidence,
   trailing stop, voice alerts, whitelist; POSTs the changes back.
   Master ARM/DISARM is in its own row with a giant red/green pill so
   the owner can never accidentally arm it.
*/
function TraderSettingsModal({ open, onClose }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(0);
  const [wlText, setWlText] = useState("");
  const [arming, setArming] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open) return;
    setErr(null);
    api.get("/trading/auto_settings").then((r) => {
      setS(r.data);
      setWlText((r.data?.whitelist || []).join(", "));
    }).catch((e) => setErr(e.response?.data?.detail || e.message));
  }, [open]);

  if (!open) return null;
  const setField = (k, v) => setS((cur) => ({ ...cur, [k]: v }));

  const save = async () => {
    if (!s) return;
    setSaving(true); setErr(null);
    try {
      const wl = wlText.split(",").map(x => x.trim().toUpperCase()).filter(Boolean);
      const r = await api.post("/trading/auto_settings", {
        per_trade_gbp: Number(s.per_trade_gbp),
        daily_cap_gbp: Number(s.daily_cap_gbp),
        max_open_positions: Number(s.max_open_positions),
        min_confidence_pct: Number(s.min_confidence_pct),
        stop_loss_pips: Number(s.stop_loss_pips),
        take_profit_pips: Number(s.take_profit_pips),
        cooldown_minutes: Number(s.cooldown_minutes),
        voice_alerts: !!s.voice_alerts,
        trailing_stop: !!s.trailing_stop,
        strategy_mode: (s.strategy_mode === "mean_reversion" ? "mean_reversion" : "pattern"),
        whitelist: wl,
      });
      setS(r.data);
      setSavedAt(Date.now());
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  const toggleArm = async () => {
    setArming(true); setErr(null);
    try {
      const path = s.armed ? "/trading/auto_stop" : "/trading/auto_start";
      const r = await api.post(path, {});
      setS((cur) => ({ ...cur, ...r.data }));
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setArming(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center p-4"
      style={{ background: "rgba(7,17,33,0.85)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
      data-testid="trader-settings-overlay"
    >
      <div
        className="w-full max-w-2xl rounded-lg overflow-hidden"
        style={{ background: T.bg2, border: `1px solid ${T.borderHi}`, boxShadow: "0 0 60px rgba(34,211,238,0.25)" }}
        onClick={(e) => e.stopPropagation()}
        data-testid="trader-settings-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3" style={{ borderBottom: `1px solid ${T.border}` }}>
          <div className="flex items-center gap-2">
            <Settings size={18} style={{ color: T.cyan }}/>
            <h2 className="text-base font-semibold" style={{ color: "#fff" }}>Auto-Trader Settings</h2>
          </div>
          <button onClick={onClose} className="text-2xl leading-none px-2 hover:opacity-100" style={{ color: T.textDim, opacity: 0.7 }} data-testid="trader-settings-close">×</button>
        </div>

        {!s ? (
          <div className="p-8 text-center text-sm" style={{ color: T.textDim }}>
            {err ? `Failed to load: ${err}` : "Loading settings…"}
          </div>
        ) : (
          <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto" style={{ color: "#e2e8f0" }}>
            {/* MASTER ARM / DISARM */}
            <div className="flex items-center justify-between p-3 rounded" style={{ background: s.armed ? "rgba(34,197,94,0.10)" : "rgba(239,68,68,0.10)", border: `1px solid ${s.armed ? "rgba(34,197,94,0.5)" : "rgba(239,68,68,0.5)"}` }}>
              <div>
                <div className="text-xs uppercase tracking-widest" style={{ color: T.textDim }}>Master switch</div>
                <div className="text-lg font-bold" style={{ color: s.armed ? "#22c55e" : "#ef4444" }} data-testid="trader-arm-state">
                  {s.armed ? "● ARMED — placing live trades" : "○ DISARMED — safe"}
                </div>
              </div>
              <button
                onClick={toggleArm}
                disabled={arming}
                data-testid="trader-arm-toggle"
                className="px-5 py-2 rounded font-semibold text-sm uppercase tracking-wider transition-all"
                style={{
                  background: s.armed ? "#ef4444" : "#22c55e", color: "#0a1628",
                  opacity: arming ? 0.5 : 1, cursor: arming ? "wait" : "pointer",
                  boxShadow: s.armed ? "0 0 14px rgba(239,68,68,0.6)" : "0 0 14px rgba(34,197,94,0.6)",
                }}
              >{arming ? "…" : (s.armed ? "Disarm" : "Arm")}</button>
            </div>

            {/* 🚨 PANIC BUTTON — closes ALL open positions on Capital instantly */}
            <PanicButton openCount={s.open_positions || 0} onDone={() => {
              // Refresh settings after panic so UI shows new state
              api.get("/trading/auto_settings").then((r) => setS(r.data)).catch(() => {});
            }} />

            {/* 📊 BACKTEST — validate the deterministic strategy edge before
                arming. Added Feb 2026 after the engine revealed massive
                losses on 30+ of the original 36 whitelisted symbols. */}
            <BacktestButton />

            {/* 📋 STRATEGY EDGE — pinned reference card so the owner
                always knows what the bot is doing, what to expect, and
                when to review. Added Feb 2026. */}
            <StrategyEdgeCard />

            {/* 🧠 STRATEGY MODE — owner asked "chance it to the best
                strategy" (Feb 2026). Both engines coexist; owner picks
                which fires. Live money warning shown when switching. */}
            <div className="rc-panel p-3">
              <div className="text-xs uppercase tracking-widest mb-2" style={{ color: T.textDim }}>
                Strategy Engine
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  data-testid="strategy-mode-pattern"
                  onClick={() => setField("strategy_mode", "pattern")}
                  className="p-2 text-left transition-all"
                  style={{
                    background: (s.strategy_mode || "pattern") === "pattern" ? "rgba(34,211,238,0.14)" : "rgba(7,17,33,0.5)",
                    border: `1px solid ${(s.strategy_mode || "pattern") === "pattern" ? T.cyan : T.border}`,
                    color: T.text,
                  }}
                >
                  <div className="text-[11px] font-bold" style={{ color: T.cyan }}>6-PATTERN + LLM</div>
                  <div className="text-[10px] mt-1 leading-snug" style={{ color: T.textDim }}>
                    Classical TA (triangles, flags, double tops/bottoms) on 15m bars, filtered by SMA-20 + RSI-14 + Claude second-opinion. 1:3 R:R.
                  </div>
                </button>
                <button
                  type="button"
                  data-testid="strategy-mode-meanrev"
                  onClick={() => setField("strategy_mode", "mean_reversion")}
                  className="p-2 text-left transition-all"
                  style={{
                    background: s.strategy_mode === "mean_reversion" ? "rgba(52,211,153,0.14)" : "rgba(7,17,33,0.5)",
                    border: `1px solid ${s.strategy_mode === "mean_reversion" ? T.green : T.border}`,
                    color: T.text,
                  }}
                >
                  <div className="text-[11px] font-bold" style={{ color: T.green }}>MEAN REVERSION</div>
                  <div className="text-[10px] mt-1 leading-snug" style={{ color: T.textDim }}>
                    RSI oversold/overbought bounce off SMA-20. Higher win rate (~60-70% in ranges) but tighter 1:1 R:R.
                  </div>
                </button>
              </div>
              <div className="text-[9px] mt-2" style={{ color: T.textDim }}>
                ⚠️ On live money. Backtest via <code>POST /api/trading/backtest</code> with{" "}
                <code>{`{strategy_mode:"${s.strategy_mode === "mean_reversion" ? "mean_reversion" : "pattern"}"}`}</code> before arming.
              </div>
            </div>

            {/* MONEY GRID */}
            <div className="grid grid-cols-2 gap-3">
              <NumberField label="Per trade (£)" testid="field-per-trade" value={s.per_trade_gbp} onChange={(v) => setField("per_trade_gbp", v)} step={1} min={1}/>
              <NumberField label="Daily cap (£)" testid="field-daily-cap" value={s.daily_cap_gbp} onChange={(v) => setField("daily_cap_gbp", v)} step={10} min={10}/>
              <NumberField label="Max open positions" testid="field-max-open" value={s.max_open_positions} onChange={(v) => setField("max_open_positions", v)} step={1} min={1} max={20}/>
              <NumberField label="Min confidence %" testid="field-min-conf" value={s.min_confidence_pct} onChange={(v) => setField("min_confidence_pct", v)} step={0.5} min={0}/>
              <NumberField label="Stop-loss (pips)" testid="field-sl" value={s.stop_loss_pips} onChange={(v) => setField("stop_loss_pips", v)} step={1} min={1}/>
              <NumberField label="Take-profit (pips)" testid="field-tp" value={s.take_profit_pips} onChange={(v) => setField("take_profit_pips", v)} step={1} min={1}/>
              <NumberField label="Cooldown (mins)" testid="field-cooldown" value={s.cooldown_minutes} onChange={(v) => setField("cooldown_minutes", v)} step={5} min={1}/>
              <div className="flex flex-col gap-2">
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input type="checkbox" checked={!!s.voice_alerts} onChange={(e) => setField("voice_alerts", e.target.checked)} data-testid="field-voice" />
                  <span>Voice alerts on open/close</span>
                </label>
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input type="checkbox" checked={!!s.trailing_stop} onChange={(e) => setField("trailing_stop", e.target.checked)} data-testid="field-trailing" />
                  <span>Trailing stop (locks profit)</span>
                </label>
              </div>
            </div>

            {/* WHITELIST */}
            <div>
              <div className="text-xs uppercase tracking-widest mb-1" style={{ color: T.textDim }}>
                Whitelist — only these symbols will trade ({wlText.split(",").filter(x => x.trim()).length} items)
              </div>
              <textarea
                value={wlText}
                onChange={(e) => setWlText(e.target.value)}
                className="w-full text-xs p-2 rounded font-mono"
                rows={4}
                style={{ background: "rgba(7,17,33,0.7)", border: `1px solid ${T.border}`, color: "#e2e8f0" }}
                data-testid="field-whitelist"
                placeholder="GOLD, BTCUSD, NVDA, …"
              />
              <div className="text-[10px] mt-1" style={{ color: T.textDim }}>
                Comma-separated. Locked to Capital.com-tradable instruments. Empty list = no trades.
              </div>
            </div>

            {/* CURRENT STATE READOUT */}
            <div className="grid grid-cols-2 gap-3 text-[11px] p-3 rounded" style={{ background: "rgba(7,17,33,0.5)" }}>
              <div><span style={{ color: T.textDim }}>Spent today:</span> <strong>£{(s.spent_today_gbp || 0).toFixed(2)}</strong> / £{s.daily_cap_gbp}</div>
              <div><span style={{ color: T.textDim }}>Open positions:</span> <strong>{s.open_positions ?? 0}</strong> / {s.max_open_positions}</div>
            </div>

            {/* 📊 PERFORMANCE STATS — every trade recorded, wins/losses, win rate */}
            {s.stats && (
              <div className="rounded p-3" style={{ background: "rgba(7,17,33,0.5)", border: `1px solid ${T.border}` }} data-testid="trader-stats">
                <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: T.textDim }}>📊 Performance — All Time</div>

                {/* 📈 Equity curve — last 30 days cumulative P&L */}
                {(s.stats.equity_curve || []).length > 1 ? (
                  <div className="h-20 -mx-1 mb-3" data-testid="equity-curve">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={s.stats.equity_curve} margin={{ top: 2, right: 4, left: 4, bottom: 0 }}>
                        <defs>
                          <linearGradient id="eq-grad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#22d3ee" stopOpacity={0.55}/>
                            <stop offset="100%" stopColor="#22d3ee" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <XAxis dataKey="date" hide/>
                        <YAxis hide domain={["auto", "auto"]}/>
                        <Tooltip
                          contentStyle={{ background: "#0a1628", border: `1px solid ${T.borderHi}`, fontSize: 10, padding: "4px 6px" }}
                          labelStyle={{ color: T.textDim }}
                          itemStyle={{ color: "#22d3ee" }}
                          formatter={(v) => [`£${Number(v).toFixed(2)}`, "Cumulative"]}
                        />
                        <Area type="monotone" dataKey="cumulative" stroke="#22d3ee" strokeWidth={2} fill="url(#eq-grad)"/>
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="text-[9px] text-center pb-2" style={{ color: T.textDim }} data-testid="equity-curve-empty">
                    Equity curve will appear after a couple of closed trades…
                  </div>
                )}

                <div className="grid grid-cols-4 gap-3 text-center">
                  <div>
                    <div className="text-2xl font-bold" style={{ color: s.stats.win_rate_pct >= 50 ? "#22c55e" : "#ef4444" }} data-testid="stats-winrate">
                      {s.stats.win_rate_pct}%
                    </div>
                    <div className="text-[9px]" style={{ color: T.textDim }}>WIN RATE</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold" style={{ color: "#22c55e" }} data-testid="stats-wins">{s.stats.wins}</div>
                    <div className="text-[9px]" style={{ color: T.textDim }}>WINS</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold" style={{ color: "#ef4444" }} data-testid="stats-losses">{s.stats.losses}</div>
                    <div className="text-[9px]" style={{ color: T.textDim }}>LOSSES</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold" style={{ color: (s.stats.total_pnl_gbp || 0) >= 0 ? "#22c55e" : "#ef4444" }} data-testid="stats-pnl">
                      £{(s.stats.total_pnl_gbp || 0) >= 0 ? "+" : ""}{(s.stats.total_pnl_gbp || 0).toFixed(2)}
                    </div>
                    <div className="text-[9px]" style={{ color: T.textDim }}>TOTAL P&amp;L</div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 text-[10px] text-center mt-3 pt-3" style={{ borderTop: `1px solid ${T.border}`, color: T.textDim }}>
                  <div><strong style={{ color: "#e2e8f0" }} data-testid="stats-open">{s.stats.open_positions}</strong> open now</div>
                  <div><strong style={{ color: "#e2e8f0" }} data-testid="stats-closed">{s.stats.closed_total}</strong> closed</div>
                  <div><strong style={{ color: "#e2e8f0" }} data-testid="stats-placed">{s.stats.placed_total}</strong> placed all-time</div>
                </div>
                {s.stats.breakevens > 0 && (
                  <div className="text-[9px] mt-2 text-center" style={{ color: T.textDim }}>
                    + {s.stats.breakevens} breakeven · {s.stats.rejected_total} signals rejected (cap/whitelist/cooldown)
                  </div>
                )}
              </div>
            )}

            {/* ERROR */}
            {err && <div className="text-xs p-2 rounded" style={{ background: "rgba(239,68,68,0.1)", color: "#fca5a5" }} data-testid="trader-settings-error">{err}</div>}

            {/* FOOTER */}
            <div className="flex items-center justify-end gap-3 pt-2" style={{ borderTop: `1px solid ${T.border}` }}>
              {savedAt > 0 && Date.now() - savedAt < 3000 && (
                <span className="text-xs" style={{ color: "#22c55e" }} data-testid="trader-settings-saved">✓ Saved</span>
              )}
              <button onClick={onClose} className="px-4 py-2 text-sm rounded" style={{ background: "transparent", border: `1px solid ${T.border}`, color: T.textDim }} data-testid="trader-settings-cancel">Close</button>
              <button onClick={save} disabled={saving} className="px-4 py-2 text-sm rounded font-semibold" style={{ background: T.cyan, color: T.bg, opacity: saving ? 0.5 : 1 }} data-testid="trader-settings-save">{saving ? "Saving…" : "Save"}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function NumberField({ label, value, onChange, step = 1, min, max, testid }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-widest" style={{ color: T.textDim }}>{label}</span>
      <input
        type="number"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        step={step} min={min} max={max}
        className="px-2 py-1.5 rounded text-sm"
        style={{ background: "rgba(7,17,33,0.7)", border: `1px solid ${T.border}`, color: "#e2e8f0" }}
        data-testid={testid}
      />
    </label>
  );
}

/* ───── 🚨 PANIC button — closes every open Capital position immediately
   and disarms the auto-trader so it doesn't reopen them. Two-step confirm
   so the owner can't trip it on a misclick. ───── */
function PanicButton({ openCount, onDone }) {
  const [stage, setStage] = useState("idle"); // idle | confirm | firing | done
  const [result, setResult] = useState(null);

  const fire = async () => {
    setStage("firing");
    try {
      const r = await api.post("/trading/panic_close_all", {});
      setResult(r.data);
      setStage("done");
      onDone?.();
    } catch (e) {
      setResult({ error: e.response?.data?.detail || e.message });
      setStage("done");
    }
  };

  if (stage === "done") {
    const ok = result?.ok;
    return (
      <div className="p-3 rounded text-xs" style={{ background: ok ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)", border: `1px solid ${ok ? "rgba(34,197,94,0.5)" : "rgba(239,68,68,0.5)"}` }} data-testid="panic-result">
        {ok ? (
          <span style={{ color: "#22c55e" }}>✓ Closed {result.closed_count} positions, auto-trader disarmed.</span>
        ) : (
          <span style={{ color: "#ef4444" }}>⚠ {result?.error || `Closed ${result?.closed_count}, ${result?.failed_count} failed`}</span>
        )}
        <button onClick={() => setStage("idle")} className="ml-3 text-[10px] underline" style={{ color: T.textDim }}>reset</button>
      </div>
    );
  }

  if (stage === "confirm") {
    return (
      <div className="p-3 rounded flex items-center justify-between gap-3" style={{ background: "rgba(239,68,68,0.18)", border: "1px solid #ef4444" }} data-testid="panic-confirm">
        <span className="text-xs" style={{ color: "#fca5a5" }}>
          Close ALL {openCount} open positions on Capital RIGHT NOW?
        </span>
        <div className="flex gap-2">
          <button onClick={() => setStage("idle")} className="px-3 py-1 text-xs rounded" style={{ border: `1px solid ${T.border}`, color: T.textDim }}>Cancel</button>
          <button onClick={fire} className="px-3 py-1 text-xs rounded font-bold" style={{ background: "#ef4444", color: "#fff" }} data-testid="panic-confirm-yes">Yes, close all</button>
        </div>
      </div>
    );
  }

  return (
    <button
      onClick={() => setStage("confirm")}
      disabled={stage === "firing" || openCount === 0}
      data-testid="panic-button"
      className="w-full py-2 rounded font-bold text-xs uppercase tracking-widest transition-all"
      style={{
        background: openCount > 0 ? "rgba(239,68,68,0.15)" : "rgba(100,116,139,0.1)",
        border: `1px solid ${openCount > 0 ? "#ef4444" : T.border}`,
        color: openCount > 0 ? "#ef4444" : T.textDim,
        cursor: openCount > 0 ? "pointer" : "not-allowed",
      }}
    >
      🚨 PANIC — Close all {openCount} open position{openCount === 1 ? "" : "s"}
    </button>
  );
}


/* ───── 📋 STRATEGY EDGE CARD — pinned reference card on /trading so the
   owner can see exactly what the bot is supposed to do, what the
   backtested edge looks like, and what realistic returns to expect.
   Added Feb 2026 after the owner asked "can you add all this info
   somewhere on the app?". Pure static reference — numbers come from
   the validated 3-week backtest at HOUR resolution with the trimmed
   7-symbol whitelist + 1:3 R:R. If the strategy/whitelist changes,
   keep these numbers in sync. */
function StrategyEdgeCard() {
  // Owner Feb 2026: "naturally just re-calculate all the calculations from
  // whatever money is ever in my capital account." Card now pulls the LIVE
  // Capital.com balance on mount + every 60s, derives a sensible default
  // stake (~1% risk-per-trade), and recomputes weekly/monthly projections
  // from THAT. Owner can still override the stake manually if they want
  // to be more aggressive — the auto-link badge stays lit either way.
  //
  // Math comes from the validated 3-week backtest:
  //   • 29.7% win rate · expectancy +2.79 pips/trade
  //   • ~57 qualifying trades per week
  //   • £/pip varies by instrument (CFDs, FX, indices) so we use a
  //     conservative 0.6–1.0× ratio per £1 of stake (matches the
  //     £60–£100/wk figure we calibrated at £10 stake).
  const [balance, setBalance] = useState(null);
  const [linked, setLinked] = useState(true);  // auto-track Capital balance
  const [stake, setStake] = useState(10);

  // Poll Capital balance every 60s while card is open
  useEffect(() => {
    let cancelled = false;
    const pull = async () => {
      try {
        const r = await api.get("/jarvis/capital_pnl");
        if (cancelled) return;
        const bal = r.data?.account?.balance;
        if (typeof bal === "number") setBalance(bal);
      } catch { /* ignore — orb may be offline */ }
    };
    pull();
    const id = setInterval(pull, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Auto-derive stake from balance when linked. Standard money-management
  // rule: risk ~1% per trade. With our 15-pip stop @ ~£0.10/pip per £1 of
  // stake, 1% of account ≈ stake × 1.5, so stake ≈ balance / 150.
  // Use balance / 100 for a slightly more active (~1.5%) profile by
  // default — matches our backtest expectancy frame.
  const derivedStake = balance && balance > 0 ? Math.max(1, Math.round(balance / 100)) : 10;
  useEffect(() => {
    if (linked) setStake(derivedStake);
  }, [linked, derivedStake]);

  const safeStake = Math.max(1, Number(stake) || 0);
  const dailyCap = safeStake * 10;                 // 10 trades max/day
  const winPerTrade = +(safeStake * 4.5).toFixed(2);   // 45-pip TP × £0.1/pip per £1 stake
  const lossPerTrade = +(safeStake * 1.5).toFixed(2);  // 15-pip stop × £0.1/pip per £1 stake
  const weeklyLow  = Math.round(safeStake * 6);    // conservative estimate
  const weeklyHigh = Math.round(safeStake * 10);   // upper estimate
  const monthlyLow  = weeklyLow * 4;
  const monthlyHigh = weeklyHigh * 4;
  const yearlyLow  = monthlyLow * 12;
  const yearlyHigh = monthlyHigh * 12;
  const accountFor1Pct = Math.round(safeStake * 100); // 1% risk-per-trade rule of thumb
  const accountFor2Pct = Math.round(safeStake * 50);  // 2% risk-per-trade rule of thumb
  const riskPct = balance && balance > 0 ? ((lossPerTrade / balance) * 100).toFixed(2) : null;

  return (
    <div className="rc-panel p-4 space-y-3" data-testid="strategy-edge-card">
      <div className="font-display tracking-widest text-[var(--rc-gold)] rc-glow-text uppercase text-xs">
        // STRATEGY EDGE · BACKTESTED
      </div>

      {/* LIVE BALANCE BADGE */}
      <div className="border border-[var(--rc-gold-dim)] bg-[rgba(0,4,12,0.6)] p-3 flex items-center justify-between gap-3"
           data-testid="strategy-live-balance">
        <div>
          <div className="rc-label text-[9px]">// LIVE CAPITAL.COM BALANCE</div>
          <div className="font-display text-xl text-[var(--rc-gold)] tabular-nums leading-tight">
            {balance == null ? "loading…" : `£${balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setLinked((v) => !v)}
          className={`text-[10px] font-display tracking-widest px-2 py-1.5 transition-colors ${
            linked
              ? "bg-[rgba(34,211,238,0.18)] border border-[#22d3ee] text-[#22d3ee]"
              : "border border-[var(--rc-gold-dim)] text-[var(--rc-gold)] opacity-70 hover:opacity-100"
          }`}
          title={linked ? "Currently auto-tracking your live balance. Click to manually override stake." : "Click to re-link stake to live balance"}
          data-testid="strategy-link-balance"
        >
          {linked ? "🔗 AUTO-LINKED" : "✋ MANUAL"}
        </button>
      </div>

      {/* INTERACTIVE STAKE INPUT */}
      <div className="border border-[var(--rc-gold)] p-3 bg-[rgba(212,175,55,0.06)]">
        <div className="flex items-center justify-between mb-1.5">
          <div className="rc-label text-[9px]">// STAKE PER TRADE</div>
          {riskPct && (
            <div className="text-[10px] opacity-70">
              risk per trade: <strong className={`tabular-nums ${parseFloat(riskPct) <= 2 ? "text-green-400" : "text-red-400"}`}>{riskPct}%</strong> of balance
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="font-display text-2xl text-[var(--rc-gold)]">£</span>
          <input
            type="number"
            min="1"
            step="5"
            value={stake}
            onChange={(e) => { setLinked(false); setStake(e.target.value); }}
            className="flex-1 bg-transparent border border-[var(--rc-gold-dim)] focus:border-[var(--rc-gold)] outline-none px-2 py-1.5 font-display text-2xl tabular-nums text-[var(--rc-gold)]"
            data-testid="strategy-stake-input"
          />
        </div>
        <div className="flex gap-1 mt-2">
          {[10, 25, 50, 100, 250].map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => { setLinked(false); setStake(v); }}
              className={`flex-1 text-[10px] py-1 font-display tracking-widest transition-colors ${
                Number(stake) === v
                  ? "border border-[var(--rc-gold)] bg-[rgba(212,175,55,0.18)] text-[var(--rc-gold)]"
                  : "border border-[var(--rc-gold-dim)] text-[var(--rc-gold)] opacity-60 hover:opacity-100"
              }`}
              data-testid={`stake-preset-${v}`}
            >
              £{v}
            </button>
          ))}
        </div>
        <div className="text-[10px] opacity-60 mt-2 leading-snug">
          {linked
            ? <>Auto-set from your <strong className="text-[var(--rc-gold)]">£{(balance || 0).toLocaleString()}</strong> balance · {`~`}1.5% risk-per-trade. Bigger balance → bigger stake → bigger weekly P&amp;L. Untouch.</>
            : <>Manual stake. Recommended account: <strong className="text-[var(--rc-gold)]">£{accountFor2Pct.toLocaleString()}</strong> (2% risk) to <strong className="text-[var(--rc-gold)]">£{accountFor1Pct.toLocaleString()}</strong> (1%, safer).</>
          }
        </div>
      </div>

      {/* HEADLINE NUMBERS — RECOMPUTED LIVE */}
      <div className="grid grid-cols-2 gap-2" data-testid="strategy-headline-stats">
        <Stat label="WIN RATE TARGET"    value="29.7%"                                       tone="ok" />
        <Stat label="EXPECTANCY"          value="+2.79 pips"                                  tone="ok" />
        <Stat label="EXPECTED / WEEK"     value={`£${weeklyLow}–£${weeklyHigh}`}              tone="ok" />
        <Stat label="EXPECTED / MONTH"    value={`£${monthlyLow.toLocaleString()}–£${monthlyHigh.toLocaleString()}`} tone="ok" />
        <Stat label="EXPECTED / YEAR"     value={`£${yearlyLow.toLocaleString()}–£${yearlyHigh.toLocaleString()}`} tone="ok" />
        <Stat label="WIN PAYS"            value={`+£${winPerTrade.toLocaleString()}`}                          tone="ok" />
        <Stat label="LOSS COSTS"          value={`-£${lossPerTrade.toLocaleString()}`}                         tone="bad" />
        <Stat label="DAILY CAP"           value={`£${dailyCap.toLocaleString()}`}                            />
      </div>

      {/* Strategy guts — what actually fires a trade */}
      <div className="border border-[var(--rc-gold-dim)] p-3 text-[11px] leading-relaxed space-y-1.5">
        <div className="rc-label text-[9px] mb-1">// HOW IT DECIDES</div>
        <div><span className="opacity-60">1.</span> Scans 30 whitelisted CFDs every 60s on <strong className="text-[var(--rc-gold)]">15-minute charts</strong></div>
        <div><span className="opacity-60">2.</span> Looks for <strong className="text-[var(--rc-gold)]">6 chart patterns</strong> (triangles, flags, double tops/bottoms)</div>
        <div><span className="opacity-60">3.</span> Must agree with the <strong className="text-[var(--rc-gold)]">SMA-20 trend</strong> (no buys below, no sells above)</div>
        <div><span className="opacity-60">4.</span> Skips if <strong className="text-[var(--rc-gold)]">RSI-14 extreme</strong> (overbought buys, oversold sells)</div>
        <div><span className="opacity-60">5.</span> <strong className="text-[var(--rc-gold)]">Claude (Sonnet 4.5)</strong> reviews the setup — vetoes weak ones</div>
        <div><span className="opacity-60">6.</span> Fires {linked ? "50%-of-balance" : `£${safeStake}`} trade with <strong className="text-[var(--rc-gold)]">1:1.67 risk/reward</strong> (15-pip stop, 25-pip TP)</div>
      </div>

      {/* Whitelist */}
      <div className="border border-[var(--rc-gold-dim)] p-3">
        <div className="rc-label text-[9px] mb-1.5">// 7 PROVEN WINNERS (the only symbols the bot trades)</div>
        <div className="flex flex-wrap gap-1">
          {["US100","NASDAQ","TSLA","NVDA","AAPL","GOLD","XAUUSD"].map((s) => (
            <span key={s} className="px-2 py-0.5 border border-[var(--rc-gold)] text-[10px] font-display tracking-widest text-[var(--rc-gold)]">
              {s}
            </span>
          ))}
        </div>
        <div className="text-[10px] opacity-50 mt-2">
          Trimmed from 36 → 7 after backtesting showed the other 30 lost <strong>-6,855 pips</strong> in 3 weeks.
        </div>
      </div>

      {/* Reminder */}
      <div className="border border-[var(--rc-gold-dim)] p-3 text-[10.5px] leading-relaxed opacity-90">
        <div className="rc-label text-[9px] mb-1">// 2-WEEK REVIEW</div>
        Let the bot run for 2 weeks before tweaking. Then ask the orb
        <span className="text-[var(--rc-gold)]"> {`"trader scorecard"`} </span>
        and we&apos;ll pull the real numbers vs these predictions. If
        win&nbsp;rate ≥&nbsp;25% holds → scale stake. If under 20% → trim
        whitelist further.
      </div>

      <div className="text-[10px] opacity-50 italic">
        Numbers from 3-week historical Capital.com backtest, HOUR resolution.
        £/pip estimates assume conservative ~£0.10/pip per £1 stake (CFD average).
        Real returns vary by instrument volatility and broker spread.
      </div>
    </div>
  );
}




/* ───── 📊 BACKTEST button — replays the live strategy against historical
   Capital.com bars so the owner can validate the deterministic edge
   BEFORE arming real money. Added Feb 2026 after the backtester revealed
   a -6,855-pip flaw in the previous 36-symbol whitelist; cutting down to
   5 winners turned -£6,855 into +£345 over the same window. */
function BacktestButton() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  const run = async () => {
    setRunning(true); setErr(null); setResult(null);
    try {
      const r = await api.post("/trading/backtest", { resolution: "MINUTE_15", max_bars: 500 });
      setResult(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Backtest failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={run}
        disabled={running}
        className="w-full text-left font-display text-xs tracking-widest uppercase px-3 py-3 rounded transition-colors disabled:opacity-50"
        style={{
          background: running ? "rgba(34,211,238,0.05)" : "rgba(34,211,238,0.12)",
          border: "1px solid #22d3ee",
          color: "#22d3ee",
        }}
        data-testid="backtest-button"
      >
        {running ? "⏳ Running backtest (≈10s)…" : "📊 RUN BACKTEST — validate strategy"}
      </button>

      {err && (
        <div className="p-2 rounded text-xs"
             style={{ background: "rgba(239,68,68,0.12)", border: "1px solid #ef4444", color: "#fca5a5" }}
             data-testid="backtest-error">
          ⚠ {err}
        </div>
      )}

      {result && !err && (
        <div className="p-3 rounded text-xs space-y-2"
             style={{ background: "rgba(0,4,12,0.6)", border: "1px solid var(--rc-gold-dim)" }}
             data-testid="backtest-result">
          <div className="grid grid-cols-3 gap-2">
            <Stat label="WIN RATE"   value={`${result.win_rate_pct}%`} tone={result.win_rate_pct >= 25 ? "ok" : "bad"} />
            <Stat label="NET PIPS"   value={result.total_pips > 0 ? `+${result.total_pips}` : `${result.total_pips}`} tone={result.total_pips >= 0 ? "ok" : "bad"} />
            <Stat label="EXPECTANCY" value={`${result.expectancy_per_trade > 0 ? "+" : ""}${result.expectancy_per_trade}`} tone={result.expectancy_per_trade >= 0 ? "ok" : "bad"} />
            <Stat label="TRADES"     value={result.total_trades} />
            <Stat label="MAX DD"     value={`${result.max_drawdown_pips}`} tone="bad" />
            <Stat label="W / L"      value={`${result.wins}/${result.losses}`} />
          </div>
          <div className="text-[10px] opacity-60 mt-1">
            Tested {result.symbols_tested} symbols · {result.resolution} bars · last ~{result.max_bars} candles · stop {result.settings_used?.stop_loss_pips}p / TP {result.settings_used?.take_profit_pips}p
          </div>
          {Array.isArray(result.per_symbol) && result.per_symbol.length > 0 && (
            <details className="text-[10px] mt-2" data-testid="backtest-per-symbol">
              <summary className="cursor-pointer opacity-80 hover:opacity-100">Per-symbol breakdown</summary>
              <table className="w-full mt-2 tabular-nums">
                <thead>
                  <tr className="opacity-50">
                    <th className="text-left pr-2">Symbol</th>
                    <th className="text-right">Tr</th>
                    <th className="text-right">Win%</th>
                    <th className="text-right pl-2">Pips</th>
                  </tr>
                </thead>
                <tbody>
                  {result.per_symbol
                    .filter((p) => !p.skipped && p.total_trades > 0)
                    .sort((a, b) => b.total_pips - a.total_pips)
                    .map((p) => (
                      <tr key={p.symbol}>
                        <td className="pr-2 font-display">{p.symbol}</td>
                        <td className="text-right">{p.total_trades}</td>
                        <td className="text-right">{p.win_rate_pct}%</td>
                        <td className={`text-right pl-2 ${p.total_pips >= 0 ? "text-green-400" : "text-red-400"}`}>{p.total_pips > 0 ? `+${p.total_pips}` : p.total_pips}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone }) {
  const cls =
    tone === "ok"  ? "text-green-400" :
    tone === "bad" ? "text-red-400"   :
                     "text-[var(--rc-gold)]";
  return (
    <div className="border border-[var(--rc-gold-dim)] p-2 min-w-0 overflow-hidden">
      <div className="rc-label text-[9px] truncate">{label}</div>
      <div className={`font-display text-sm mt-0.5 tabular-nums break-words leading-tight ${cls}`}>{value}</div>
    </div>
  );
}





/* ───── Capital live ticker rail (left side) ─────
   Vertical scrolling rail showing live prices for every instrument the
   owner can trade on Capital.com. Polls /api/trading/capital_watchlist
   every 5 seconds. Owner brief Feb 2026: "live stream data of all them
   apps … in a line down the left side of my trading platform". */
function CapitalTickerRail() {
  const [items, setItems] = useState([]);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    const fetchOnce = () => {
      api.get("/trading/capital_watchlist").then((r) => {
        if (!alive) return;
        setItems(r.data?.items || []);
        if (r.data?.error) setErr(r.data.error);
        else setErr(null);
      }).catch((e) => { if (alive) setErr(e.response?.data?.detail || e.message); });
    };
    fetchOnce();
    const id = setInterval(fetchOnce, 5000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  return (
    <div
      className="flex flex-col flex-shrink-0 overflow-y-auto"
      style={{
        width: 130,
        background: "rgba(7,17,33,0.92)",
        borderRight: `1px solid ${T.border}`,
        scrollbarWidth: "thin",
      }}
      data-testid="capital-ticker-rail"
    >
      <div
        className="sticky top-0 z-10 px-2 py-1.5 text-[9px] tracking-widest uppercase text-center"
        style={{ background: "rgba(7,17,33,0.98)", color: T.cyan, borderBottom: `1px solid ${T.border}` }}
      >
        LIVE · CAPITAL
      </div>
      {err && (
        <div className="text-[9px] p-2 text-center" style={{ color: "#ef4444" }} data-testid="rail-error">
          {err === "capital_not_configured" ? "Capital API not configured" : err}
        </div>
      )}
      {items.length === 0 && !err && (
        <div className="text-[9px] p-2 text-center" style={{ color: T.textDim }}>Loading…</div>
      )}
      {items.map((it) => {
        const up = (it.change_pct || 0) >= 0;
        const colour = up ? "#22c55e" : "#ef4444";
        return (
          <div
            key={it.epic + it.symbol}
            className="px-2 py-1.5 flex flex-col gap-0.5 cursor-pointer transition-all"
            style={{ borderBottom: `1px solid ${T.border}` }}
            data-testid={`rail-${it.symbol}`}
            onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(34,211,238,0.08)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            title={`${it.symbol} · bid ${it.bid} · ask ${it.offer} · status ${it.status}`}
          >
            <div className="flex items-center justify-between text-[10px]">
              <span style={{ color: "#e2e8f0", fontWeight: 600 }}>{it.symbol}</span>
              <span style={{ color: colour, fontWeight: 700, fontSize: 9 }}>{up ? "▲" : "▼"}</span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-[11px] font-mono" style={{ color: "#fff" }}>
                {it.price > 1000 ? it.price.toFixed(0) : it.price.toFixed(it.price < 1 ? 4 : 2)}
              </span>
              <span className="text-[9px] font-mono" style={{ color: colour }}>
                {(it.change_pct >= 0 ? "+" : "") + (it.change_pct || 0).toFixed(2)}%
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}



/* ───── Top bar with title, tabs, news ticker ───── */
function TopBar({ live, onRefresh, loading, now, onBack }) {
  return (
    <div
      className="flex items-center gap-4 px-4 py-2"
      style={{ background: "rgba(7,17,33,0.7)", borderBottom: `1px solid ${T.border}` }}
    >
      <button
        onClick={onBack}
        className="text-[11px] px-2 py-1 rounded hover:opacity-80"
        style={{ color: T.textDim, border: `1px solid ${T.border}` }}
        data-testid="trading-back-btn"
      >← BACK</button>
      <div className="flex items-center gap-2">
        <div className="w-6 h-6 rounded-sm flex items-center justify-center" style={{ background: T.cyan, color: T.bg, fontWeight: 700 }}>▲</div>
        <div className="text-[13px] font-semibold" style={{ color: T.text }}>Market Intelligence</div>
      </div>
      <div className="flex items-center gap-1 ml-2">
        <button className="text-[11px] px-2 py-1 rounded" style={{ color: T.text, background: "rgba(34,211,238,0.12)", border: `1px solid ${T.border}` }}>
          Market Feeds
        </button>
        <button className="text-[11px] px-2 py-1 rounded" style={{ color: T.textDim }}>
          Watchlist
        </button>
      </div>
      <div className="flex-1 overflow-hidden ml-2" style={{ borderLeft: `1px solid ${T.border}`, paddingLeft: 12 }}>
        <div
          className="whitespace-nowrap text-[11px]"
          style={{ animation: "mi-marquee 80s linear infinite", color: T.textDim }}
        >
          {Array(2).fill(0).map((_, i) => (
            <span key={i}>
              <span style={{ color: T.cyan }}>NEWS TICKER </span>
              The volatility: Session halt straightens market stocks 2.6%
              <span className="mx-8" style={{ color: T.cyan }}>NEWS TICKER </span>
              Sees on Americas biggest-in respects compliance 2.0%
              <span className="mx-8" style={{ color: T.cyan }}>NEWS TICKER </span>
              Fed signals rate steady at 4.5% — markets unfazed
              <span className="mx-8" style={{ color: T.cyan }}>NEWS TICKER </span>
              Tech rally continues as AI sector posts +6% week
              <span className="mx-8"/>
            </span>
          ))}
        </div>
        <style>{`@keyframes mi-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>
      </div>
      <div className="text-[11px] text-right" style={{ color: T.textDim }}>
        <div style={{ color: T.text }}>Market <span style={{ color: T.cyan }}>10:34.70</span></div>
        <div>{now.toLocaleTimeString()} · {now.toLocaleDateString()}</div>
      </div>
      <span className="text-[10px] px-2 py-0.5 rounded" data-testid="trading-status-badge"
            style={{ color: live ? T.green : T.amber, border: `1px solid ${live ? T.green : T.amber}` }}>
        ● {live ? "LIVE" : "CACHED"}
      </span>
      <button onClick={onRefresh} disabled={loading} data-testid="trading-refresh-btn"
              className="p-1 rounded hover:opacity-100" style={{ color: T.cyan, opacity: loading ? 0.5 : 1 }}>
        <RefreshCw size={14} className={loading ? "animate-spin" : ""}/>
      </button>
      <Bell size={14} style={{ color: T.textDim }}/>
      <div className="w-7 h-7 rounded-full" style={{ background: "rgba(34,211,238,0.25)", border: `1px solid ${T.border}` }}/>
    </div>
  );
}

/* ───── The AI Filtering Matrix — 3D PERSPECTIVE centrepiece ─────
 * Cards approach from the distance (translateZ(-600px) at spawn) and
 * grow as they fly into the matrix. Each card has the company symbol,
 * price and % change clearly readable. The bin is rendered as a 3D
 * isometric box with cards visibly tumbling into it. The hex nodes are
 * tilted on rotateX so they sit in the same 3D space as the river.
 * ─────────────────────────────────────────────────────────────── */
function AIFilteringMatrix({ rows }) {
  // ─── Live counters ───
  // Try to read real auto-trader stats from /api/trading/auto_log; fall
  // back to simulated counters that tick up every 7-15s so the matrix
  // always feels "alive" even before the trader is armed.
  const [stats, setStats] = useState({ buy: 0, reject: 0 });
  useEffect(() => {
    let stopped = false;
    const pull = async () => {
      try {
        const r = await api.get("/trading/auto_log?limit=200");
        const today = new Date().toISOString().slice(0, 10);
        const rows2 = (r.data?.entries) || [];
        const todays = rows2.filter(e => (e.date || "") === today);
        const buy = todays.filter(e => e.outcome === "placed").length;
        const reject = todays.filter(e => e.outcome === "rejected").length;
        if (!stopped) setStats({ buy, reject });
      } catch (_) { /* keep zeros until backend responds */ }
    };
    pull();
    const id = setInterval(pull, 15000);
    // 🚫 Removed the simulated tick — owner brief Feb 2026: "it says
    // approved trade 19 today and it hasn't placed 19 trade so that's
    // false data". Counts are now strictly from auto_trade_log so when
    // nothing's been placed the counter reads 0.
    return () => { stopped = true; clearInterval(id); };
  }, []);
  const buySignalsToday = stats.buy;
  const rejectedToday = stats.reject;
  // Capital-locked roster — these are the symbols that fly through the
  // matrix as "in-flight analysis" cards. Owner brief Feb 2026:
  // "all the cards that are flowing through the matrix box are still all
  // the old cards they need to be cards coming in and out that are on my
  // capital list".
  const COMPANY = [
    { sym: "GOLD",     name: "Gold"       },
    { sym: "NVDA",     name: "Nvidia"     },
    { sym: "MSTR",     name: "Strategy"   },
    { sym: "BTCUSD",   name: "Bitcoin"    },
    { sym: "PLATINUM", name: "Platinum"   },
    { sym: "MSFT",     name: "Microsoft"  },
    { sym: "RIOT",     name: "Riot"       },
    { sym: "OIL_CRUDE",name: "Crude Oil"  },
    { sym: "MARA",     name: "Marathon"   },
    { sym: "SILVER",   name: "Silver"     },
  ];
  // 10 in-flight cards, each ~17 seconds, heavily staggered so depth
  // makes the river feel populated without crowding.
  const PARTICLES = useMemo(() => {
    return COMPANY.map((c, i) => {
      const up = (i % 5) !== 0 && (i % 5) !== 1;
      const change = up ? +(2 + (i * 1.7) % 8).toFixed(2) : -(1 + (i * 1.3) % 6).toFixed(2);
      const price = 80 + (c.sym.charCodeAt(0) * 1.7 + i * 11) % 380;
      return { ...c, up, change, price: +price.toFixed(2), delay: -(i * 1.7) };
    });
  }, []);

  // BIG fan of ~16 cards arcing from upper-left down to centre-bottom.
  // Pool locked to Capital instruments + a few abstract codes for visual variety.
  const FEED_DECK = useMemo(() => {
    const pool = ["SR","AD","SK","SH","NX","RB","DB","GOLD","NVDA","MSTR","BTCUSD","PLATINUM","RIOT","MARA","OIL_CRUDE","SILVER","HUT"];
    return pool.slice(0, 17).map((sym, i) => ({
      sym, name: sym,
      change: +((i * 11.3) % 36 - 11).toFixed(2),
      price: +(60 + i * 22).toFixed(2),
    }));
  }, []);

  /* ── Real placed/rejected trades from db.auto_trade_log (today only).
     Owner brief Feb 2026: "they approved cards going to the right need
     to actually be trades it's actually placing, but I'm looking at the
     screen and it's approving every card when it isn't actually placing
     a trade". Animation now ONLY renders when there are REAL placements.
  */
  const [realPlaced, setRealPlaced] = useState([]);
  const [realRejected, setRealRejected] = useState([]);
  useEffect(() => {
    let alive = true;
    const pull = async () => {
      try {
        const r = await api.get("/trading/auto_log?limit=200");
        const today = new Date().toISOString().slice(0, 10);
        const entries = (r.data?.entries) || [];
        const todays = entries.filter(e => (e.date || "") === today);
        if (alive) {
          setRealPlaced(todays.filter(e => e.outcome === "placed"));
          setRealRejected(todays.filter(e => e.outcome === "rejected"));
        }
      } catch (_) { /* keep arrays as-is */ }
    };
    pull();
    const id = setInterval(pull, 15000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // ✅ "Winners" stack to the right of the matrix = REAL placed trades
  // today only. Owner brief Feb 2026: "ones that has placed it needs to
  // show in a list below that Approved Trades today". When 0 trades have
  // been placed, the stack is empty (no animations of fake winners).
  const winners = (realPlaced || []).slice(0, 4).map(e => ({
    symbol: (e.symbol || "?").toUpperCase(),
    price: Number(e.price || e.entry || 0),
    change_pct: Number(e.confidence_pct || e.change_pct || 0),
    rationale: e.reason || e.rationale || "Auto-trader placed",
  }));

  const liveSignals = useMemo(() => {
    if (!rows || !rows.length) return [];
    return rows
      .filter(r => Math.abs(r.change_pct ?? 0) >= 1)
      .slice(0, 3)
      .map((r, i) => ({
        sym: r.symbol,
        name: r.symbol,
        up: (r.change_pct ?? 0) >= 0,
        change: +(r.change_pct ?? 0).toFixed(2),
        price: +(r.price ?? 0).toFixed(2),
        delay: -(i * 5.7),
      }));
  }, [rows]);

  /* ── CHAIN data: 3 continuous streams of cards animating in a loop ──
     INPUT  — symbols flowing IN from upper-left to matrix back
     APPROVED — winners exiting matrix FRONT-RIGHT going down-right (REAL only)
     REJECT — losers sliding OUT the matrix LEFT-side into the bin (REAL only) */
  const INPUT_CHAIN = useMemo(() => {
    // 🎯 Feb 2026 — owner asked "make sure the cards going into the matrix
    // are all the cards in our new strategy set up". The live `rows` are
    // already filtered to the 30-symbol whitelist by the backend, so the
    // primary path is correct. The fallback list now mirrors the same 30
    // pattern-strategy symbols so it never shows random tickers from a
    // stale cache when Capital is offline.
    const pool = rows && rows.length
      ? rows.map(r => ({ sym: r.symbol, change: r.change_pct ?? 0, price: r.price ?? 0 }))
      : [
          { sym: "GOLD",     change:  0.45, price: 2065.00 },
          { sym: "XAUUSD",   change:  0.43, price: 2065.00 },
          { sym: "SILVER",   change: -0.62, price:   26.30 },
          { sym: "OIL_CRUDE",change: -1.20, price:   79.93 },
          { sym: "NATURALGAS", change:  3.31, price:    2.14 },
          { sym: "US100",    change:  0.45, price:21000.00 },
          { sym: "NASDAQ",   change:  0.45, price:21000.00 },
          { sym: "US500",    change:  0.32, price: 6100.00 },
          { sym: "BTCUSD",   change:  1.40, price:64210.00 },
          { sym: "ETHUSD",   change:  2.14, price: 4339.00 },
          { sym: "AAPL",     change:  1.37, price:  256.00 },
          { sym: "TSLA",     change: -1.35, price:  430.00 },
          { sym: "NVDA",     change:  5.60, price:   33.55 },
          { sym: "MSTR",     change: 20.30, price:  123.88 },
          { sym: "MARA",     change:  3.85, price:    9.80 },
          { sym: "RIOT",     change:  1.85, price:    9.80 },
        ];
    return pool.slice(0, 8).map(p => ({
      sym: p.sym,
      change: +(p.change || 0).toFixed(2),
      price: +(p.price || 0).toFixed(2),
      up: (p.change || 0) >= 0,
    }));
  }, [rows]);

  const APPROVED_CHAIN = useMemo(() => {
    // 🛑 STRICT: only render approved chain cards for REAL placed trades.
    // If nothing's been placed today, the chain is EMPTY (no fake "every
    // card approves" theatre).
    return realPlaced.slice(0, 5).map(e => ({
      sym: (e.symbol || "?").toUpperCase(),
      change: +Number(e.confidence_pct || e.change_pct || 0).toFixed(2),
      price: +Number(e.price || e.entry || 0).toFixed(2),
    }));
  }, [realPlaced]);

  const REJECT_CHAIN = useMemo(() => {
    // 🛑 STRICT: only render reject chain cards for REAL rejected signals
    // (cooldown / whitelist / daily-cap / confidence-too-low). If nothing
    // has been rejected today, the chain is EMPTY.
    return realRejected.slice(0, 3).map(e => ({
      sym: (e.symbol || "?").toUpperCase(),
      change: +Number(e.confidence_pct || e.change_pct || 0).toFixed(2),
      price: +Number(e.price || e.entry || 0).toFixed(2),
    }));
  }, [realRejected]);

  // Reusable readable card body
  const CardBody = ({ p, big }) => (
    <>
      <div className="flex items-center justify-between" style={{ marginBottom: 4 }}>
        <span style={{ color: T.text, fontSize: big ? 16 : 14, fontWeight: 700, letterSpacing: 0.5 }}>
          {p.sym}
        </span>
        <span style={{ color: p.up ? T.green : T.red, fontSize: big ? 13 : 12, fontWeight: 600 }}>
          {p.up ? "+" : ""}{Number(p.change || 0).toFixed(2)}%
        </span>
      </div>
      <div style={{ color: T.cyan, fontSize: 10, opacity: 0.85, letterSpacing: 0.3 }}>
        {p.name || ""}
      </div>
      <div style={{ color: T.textDim, fontSize: 11, marginTop: 4 }}>
        Price <span style={{ color: T.text, fontWeight: 600 }}>${Number(p.price || 0).toFixed(2)}</span>
      </div>
      <div style={{ marginTop: 5 }}>
        <Sparkline seed={(p.sym || "X").charCodeAt(0) * 3} up={p.up} width={big ? 180 : 188} height={big ? 26 : 24}/>
      </div>
    </>
  );

  return (
    <div className="relative w-full mi-3d-stage"
         style={{ height: 740, perspective: "1500px", perspectiveOrigin: "50% 40%" }}
         data-testid="ai-filtering-matrix">

      {/* No background SVG flow streams — kept clean. */}

      {/* ── BACK SUCTION SMOKE: cyan smoke/streaks pulsing INTO the
            matrix back, making it feel like the matrix is sucking air
            (and cards) in from upper-left. ── */}
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={`suction-${i}`}
             className="mi-suction-smoke"
             style={{
               animationDelay: `${-(i * 0.7).toFixed(2)}s`,
               // Each streak originates from a slightly different upper-left
               // position so they spread out (less mechanical).
               left: 80 + i * 60,
               top: 100 + (i % 3) * 50,
             }}/>
      ))}

      {/* ── FRONT BLOW-OUT STROBE: green pulse on the matrix front-right
            edge — fires every time an approved card emerges. ── */}
      <div className="mi-front-blowout"/>

      {/* No static cascade — kept clean. Live in-flight particles below
          provide motion without a messy 17-card stack. */}

      {/* ── Market Feed label (left) ── */}
      <div className="absolute pointer-events-none"
           style={{ left: 24, top: 320, zIndex: 4 }}>
        <div className="px-3 py-2 rounded font-semibold tracking-wide"
             style={{
               color: T.text, fontSize: 12,
               background: "rgba(15,30,61,0.92)",
               border: `1.5px solid rgba(255,255,255,0.25)`,
             }}>
          Market Feed
        </div>
        <svg viewBox="0 0 80 30" width="80" height="30" style={{ marginLeft: 30, marginTop: -2 }}>
          <path d="M 4 16 Q 40 4 76 18" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="1.4"/>
          <polygon points="72,14 78,18 70,22" fill="rgba(255,255,255,0.35)"/>
        </svg>
      </div>

      {/* 🚫 The STATIC peek-cascade (12 stacked queue cards upper-LEFT) was
            removed Feb 2026 — owner brief: "loads of still cards that
            aren't moving get rid of all them cards, just have the ones
            that are moving into the AI matrix box, like a chain of steel
            cards I can only see the LEFT side of coming out". The live
            INPUT_CHAIN below handles all motion now. */}

      {/* ── AI Filtering Matrix: centered 2×2 with DEPTH stack behind. ── */}
      <div className="absolute"
           style={{
             left: "52%", top: "50%",
             transform: "translate(-50%, -50%)",
             transformStyle: "preserve-3d",
             zIndex: 12,
           }}>
        <div className="font-semibold tracking-[0.22em] mb-3 text-center"
             style={{
               color: "#ffffff", fontSize: 15,
               padding: "5px 14px",
               borderRadius: 4,
               background: "rgba(7,17,33,0.85)",
               border: "1px solid rgba(34,211,238,0.45)",
               textShadow: "0 1px 2px rgba(0,0,0,0.95)",
             }}>
          AI Filtering Matrix
        </div>
        <div className="relative" style={{ width: 280, height: 280, transformStyle: "preserve-3d" }}>
          {/* Depth layers behind the 2x2 — 10 stacked layers receding
              UP-LEFT-back with internal 2×2 divider lines so each layer
              reads as a "matrix box", per owner's reference photo. */}
          {[10, 9, 8, 7, 6, 5, 4, 3, 2, 1].map((layer) => {
            const off = layer * 10;
            const cyanA = Math.max(0.18, 0.72 - layer * 0.055);
            return (
              <div key={`depth-${layer}`}
                   className="absolute rounded-lg pointer-events-none"
                   style={{
                     left: -off,
                     top:  -off * 0.45,
                     width: 280, height: 280,
                     transform: `translateZ(${-layer * 18}px)`,
                     background: "linear-gradient(135deg, rgba(15,30,61,0.88), rgba(7,17,33,0.92))",
                     border: `1.4px solid rgba(34,211,238, ${cyanA})`,
                     boxShadow: `0 0 ${Math.max(4, 16 - layer)}px rgba(34,211,238, ${cyanA * 0.55})`,
                     opacity: Math.max(0.3, 0.92 - layer * 0.06),
                     // Internal 2×2 divider lines so layer reads as a matrix
                     backgroundImage: `
                       linear-gradient(to right,
                         transparent calc(50% - 0.6px),
                         rgba(34,211,238, ${cyanA * 0.55}) calc(50% - 0.6px),
                         rgba(34,211,238, ${cyanA * 0.55}) calc(50% + 0.6px),
                         transparent calc(50% + 0.6px)
                       ),
                       linear-gradient(to bottom,
                         transparent calc(50% - 0.6px),
                         rgba(34,211,238, ${cyanA * 0.55}) calc(50% - 0.6px),
                         rgba(34,211,238, ${cyanA * 0.55}) calc(50% + 0.6px),
                         transparent calc(50% + 0.6px)
                       ),
                       linear-gradient(135deg, rgba(15,30,61,0.88), rgba(7,17,33,0.92))
                     `,
                   }}/>
            );
          })}
          {/* OPAQUE front plate — sits at the FRONT face of the matrix and
              physically blocks any input card behind from showing through. */}
          <div className="absolute rounded-lg pointer-events-none"
               style={{
                 left: 0, top: 0, width: 280, height: 280,
                 background: "linear-gradient(135deg, #0a1628 0%, #07111f 100%)",
                 border: `1.6px solid ${T.cyan}`,
                 boxShadow: "0 0 18px rgba(34,211,238,0.35), inset 0 0 22px rgba(34,211,238,0.08)",
                 zIndex: 25,
               }}/>
          {/* ⚡ AI CORE — pulsating electronic brain that lives inside the
              matrix. Owner Feb 2026: "do some cool fancy looking AI
              electronic pulsating machine matrix in the middle of the box".
              Pure CSS layers, no canvas, no extra deps. Renders on top of
              the front plate (z 26) but below the chain cards (z 30+). */}
          <div className="absolute rounded-lg pointer-events-none overflow-hidden"
               style={{ left: 0, top: 0, width: 280, height: 280, zIndex: 26 }}
               data-testid="ai-filtering-core">
            {/* layer A — radial pulsing core */}
            <div className="mi-ai-core-pulse" />
            {/* layer B — rotating hex scanner ring */}
            <div className="mi-ai-core-ring" />
            {/* layer C — counter-rotating inner ring */}
            <div className="mi-ai-core-ring-inner" />
            {/* layer D — 6×6 neuron grid that flickers asynchronously */}
            <div className="mi-ai-core-grid">
              {Array.from({ length: 36 }).map((_, idx) => (
                <span key={idx}
                      style={{ animationDelay: `${(idx * 137) % 2800}ms` }} />
              ))}
            </div>
            {/* layer E — horizontal scanline sweep */}
            <div className="mi-ai-core-scanline" />
            {/* layer F — binary rain (subtle, two columns) */}
            <div className="mi-ai-core-rain mi-ai-core-rain--a">10110010<br/>01101001<br/>11001011<br/>00110110<br/>10011010<br/>01100110</div>
            <div className="mi-ai-core-rain mi-ai-core-rain--b">01101110<br/>10101010<br/>01010101<br/>10110011<br/>11001100<br/>00111001</div>
            {/* layer G — bright central node + crosshairs */}
            <div className="mi-ai-core-node" />
            <div className="mi-ai-core-cross" />
            {/* layer H — corner brackets */}
            <span className="mi-ai-core-bracket mi-ai-core-bracket--tl" />
            <span className="mi-ai-core-bracket mi-ai-core-bracket--tr" />
            <span className="mi-ai-core-bracket mi-ai-core-bracket--bl" />
            <span className="mi-ai-core-bracket mi-ai-core-bracket--br" />
            {/* layer I — "ANALYSING" label */}
            <div className="mi-ai-core-label">// ANALYSING</div>
          </div>
          {/* 🚫 The 4 static 2×2 "analyzing" cells were removed Feb 2026 —
              owner brief: "next to the AI filter matrix box in the middle
              there loads of still cards that aren't moving get rid of all
              them cards please and just have the ones that are moving".
              The matrix is now a clean empty stage so only the moving
              chain (coming in from the upper-left) is visible inside. */}
        </div>
      </div>

      {/* ── INPUT CHAIN: cards flowing IN from upper-LEFT → matrix BACK.
            6 cards on a 14s continuous loop, staggered every ~2.3s. ── */}
      {INPUT_CHAIN.map((c, i) => (
        <div key={`in-${i}`}
             className="mi-chain-card mi-chain-in"
             style={{
               animationDelay: `${-(i * 14 / INPUT_CHAIN.length).toFixed(2)}s`,
               borderColor: "rgba(34,211,238,0.7)",
             }}
             data-testid={`chain-in-${c.sym}`}>
          <div className="flex items-center justify-between" style={{ marginBottom: 3 }}>
            <span style={{ color: T.text, fontSize: 12, fontWeight: 700 }}>{c.sym}</span>
            <span style={{ color: c.up ? T.green : T.red, fontSize: 10, fontWeight: 600 }}>
              {c.up ? "+" : ""}{c.change.toFixed(2)}%
            </span>
          </div>
          <div style={{ color: T.textDim, fontSize: 9 }}>
            <span style={{ color: T.cyan, opacity: 0.85 }}>${c.price.toFixed(2)}</span>
          </div>
          <Sparkline seed={symSeed(c.sym)} up={c.up} width={140} height={20}/>
        </div>
      ))}

      {/* ── APPROVED CHAIN: cards flowing OUT the FRONT of matrix → DOWN-RIGHT.
            5 cards on a 9s continuous loop. Positive %, cyan glow. ── */}
      {APPROVED_CHAIN.map((c, i) => (
        <div key={`ap-${i}`}
             className="mi-chain-card mi-chain-approved"
             style={{
               animationDelay: `${-(i * 9 / APPROVED_CHAIN.length).toFixed(2)}s`,
               borderColor: T.cyan,
               boxShadow: "0 0 18px rgba(34,211,238,0.55), 0 0 40px rgba(34,211,238,0.25)",
             }}
             data-testid={`chain-approved-${c.sym}`}>
          <div className="flex items-center justify-between" style={{ marginBottom: 3 }}>
            <span style={{ color: T.cyan, fontSize: 13, fontWeight: 700 }}>{c.sym}</span>
            <span style={{
              background: T.green, color: T.bg, fontSize: 7, fontWeight: 700,
              padding: "1.5px 5px", borderRadius: 3, letterSpacing: 0.5,
            }}>TRADE</span>
          </div>
          <div style={{ color: T.text, fontSize: 11, fontWeight: 600 }}>
            ${c.price.toFixed(2)} <span style={{ color: T.green, marginLeft: 4 }}>+{c.change.toFixed(2)}%</span>
          </div>
          <Sparkline seed={symSeed(c.sym) + 3} up={true} width={140} height={22}/>
        </div>
      ))}

      {/* ── REJECT CHAIN: cards sliding OUT the SIDE (left) of matrix → into BIN.
            3 cards on a 7s continuous loop. Negative %, red. ── */}
      {REJECT_CHAIN.map((c, i) => (
        <div key={`rj-${i}`}
             className="mi-chain-card mi-chain-reject"
             style={{
               animationDelay: `${-(i * 7 / REJECT_CHAIN.length).toFixed(2)}s`,
               borderColor: "rgba(239,68,68,0.85)",
               background: "linear-gradient(135deg, rgba(80,10,10,0.95), rgba(35,5,5,0.95))",
               boxShadow: "0 0 14px rgba(239,68,68,0.45)",
             }}
             data-testid={`chain-reject-${c.sym}`}>
          <div className="flex items-center justify-between" style={{ marginBottom: 3 }}>
            <span style={{ color: "#fca5a5", fontSize: 12, fontWeight: 700 }}>{c.sym}</span>
            <span style={{ color: T.red, fontSize: 10, fontWeight: 600 }}>
              {c.change.toFixed(2)}%
            </span>
          </div>
          <div style={{ color: "#fca5a5", fontSize: 9, opacity: 0.7 }}>rejected</div>
          <Sparkline seed={symSeed(c.sym)} up={false} width={140} height={20}/>
        </div>
      ))}

      {/* ── Buy Signals stack on the RIGHT (where winners arrive) ── */}
      <div className="absolute" style={{ right: 30, top: 30 }}>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded font-semibold mb-2"
             style={{ color: T.green, fontSize: 12,
                      background: "rgba(16,185,129,0.16)",
                      border: `1.5px solid ${T.green}`,
                      boxShadow: "0 0 12px rgba(16,185,129,0.3)" }}>
          ✓ Approved Trades
          <span className="ml-1 px-2 py-0.5 rounded font-bold"
                style={{ background: T.green, color: T.bg, fontSize: 12, letterSpacing: 0.5 }}>
            {buySignalsToday}
          </span>
          <span style={{ color: T.green, fontSize: 8, opacity: 0.7, letterSpacing: 1 }}>
            TODAY
          </span>
        </div>
        <div className="flex flex-col gap-2" style={{ width: 180 }}>
          {winners.map((w, i) => (
            <div key={`${w.symbol}-${i}`} className="rounded-md px-2.5 py-2"
              style={{
                background: "linear-gradient(135deg, rgba(15,30,61,0.95), rgba(7,17,33,0.95))",
                border: `2.5px solid ${T.cyan}`,
                boxShadow: `
                  0 0 18px ${T.cyan},
                  0 0 40px rgba(34,211,238,0.55),
                  0 0 80px rgba(34,211,238,0.25),
                  inset 0 0 18px rgba(34,211,238,0.15)
                `,
                animation: `mi-winner-glow 3s ease-in-out ${i * 0.3}s infinite`,
              }}>
              <div className="flex items-center justify-between">
                <span style={{ color: T.text, fontSize: 13, fontWeight: 700 }}>{w.symbol}</span>
                <span style={{ color: T.green, fontSize: 11, fontWeight: 600 }}>
                  +{(w.change_pct ?? 0).toFixed(2)}%
                </span>
              </div>
              <div style={{ color: T.textDim, fontSize: 9, marginTop: 2 }}>
                ${(w.price ?? 0).toFixed(2)}
              </div>
              <Sparkline seed={symSeed(w.symbol)} up={true} width={160} height={24}/>
            </div>
          ))}
        </div>
      </div>

      {/* ── 3D BIN: cleaner, bigger, proper trash-bin shape (not a red dot).
            z-index 17 sits ABOVE the reject chain (16) so the bin's
            opaque body HIDES the card as it descends inside.
            Owner brief (Feb 2026): "move the bin right a little so the
            cards are dropping into it" — shifted from left:510 → 540. ── */}
      <div className="absolute"
           style={{ left: 580, top: 570, width: 180, zIndex: 17 }}
           data-testid="filtered-out-bin">
        <svg viewBox="0 0 220 200" className="w-full" style={{ overflow: "visible",
             filter: "drop-shadow(0 6px 18px rgba(0,0,0,0.55)) drop-shadow(0 0 22px rgba(239,68,68,0.4))" }}>
          <defs>
            <linearGradient id="bin-front-v3" x1="0%" x2="0%" y1="0%" y2="100%">
              <stop offset="0%"  stopColor="rgba(70,15,15,0.95)"/>
              <stop offset="60%" stopColor="rgba(40,8,8,0.98)"/>
              <stop offset="100%" stopColor="rgba(20,4,4,1)"/>
            </linearGradient>
            <linearGradient id="bin-side-v3" x1="0%" x2="100%">
              <stop offset="0%"  stopColor="rgba(20,4,4,1)"/>
              <stop offset="100%" stopColor="rgba(55,10,10,0.95)"/>
            </linearGradient>
            <linearGradient id="bin-rim-v3" x1="0%" x2="100%">
              <stop offset="0%"  stopColor="#7a1a1a"/>
              <stop offset="50%" stopColor="#c44"/>
              <stop offset="100%" stopColor="#7a1a1a"/>
            </linearGradient>
            <linearGradient id="bin-inside-v3" x1="0%" x2="0%" y1="0%" y2="100%">
              <stop offset="0%"  stopColor="rgba(0,0,0,0.95)"/>
              <stop offset="100%" stopColor="rgba(40,5,5,0.9)"/>
            </linearGradient>
          </defs>

          {/* Back rim (subtle) */}
          <ellipse cx="110" cy="34" rx="86" ry="11" fill="rgba(20,4,4,0.9)" stroke="rgba(239,68,68,0.4)" strokeWidth="0.8"/>

          {/* Dark interior — receding into the bin */}
          <path d="M 24,38 Q 110,52 196,38 L 188,52 Q 110,64 32,52 Z"
                fill="url(#bin-inside-v3)"/>

          {/* Outer body — slight funnel shape (wider at top, narrower at bottom) */}
          <path d="M 24,42 L 38,182 Q 110,194 182,182 L 196,42 Z"
                fill="url(#bin-front-v3)"
                stroke="rgba(239,68,68,0.75)" strokeWidth="1.6"/>

          {/* Right-side highlight */}
          <path d="M 182,182 L 196,42 L 192,42 L 178,180 Z"
                fill="url(#bin-side-v3)"/>

          {/* Top rim (front-facing ellipse) */}
          <ellipse cx="110" cy="42" rx="88" ry="11"
                   fill="url(#bin-rim-v3)" stroke="#ef4444" strokeWidth="1.6"/>
          <ellipse cx="110" cy="42" rx="78" ry="8"
                   fill="rgba(0,0,0,0.55)"/>

          {/* Body vent lines (subtle, suggests metal) */}
          <line x1="50" y1="80"  x2="172" y2="80"  stroke="rgba(239,68,68,0.18)" strokeWidth="0.8"/>
          <line x1="52" y1="108" x2="170" y2="108" stroke="rgba(239,68,68,0.14)" strokeWidth="0.8"/>
          <line x1="54" y1="136" x2="168" y2="136" stroke="rgba(239,68,68,0.10)" strokeWidth="0.8"/>

          {/* Funnel icon inside */}
          <g opacity="0.55">
            <path d="M 88,70 L 132,70 L 116,90 L 116,108 L 104,108 L 104,90 Z"
                  fill="none" stroke="#ef4444" strokeWidth="1.6" strokeLinejoin="round"/>
          </g>

          {/* Counter sticker on the bin front */}
          <rect x="64" y="146" width="92" height="30" rx="4"
                fill="rgba(20,4,4,0.95)" stroke="#ef4444" strokeWidth="1.2"/>
          <text x="110" y="158" textAnchor="middle" fill="#fca5a5"
                fontSize="7" fontWeight="600" letterSpacing="1.1">REJECTED TODAY</text>
          <text x="110" y="171" textAnchor="middle" fill="#ef4444"
                fontSize="14" fontWeight="800">{rejectedToday}</text>
        </svg>
        <div className="text-center font-semibold mt-1"
             style={{ color: T.red, fontSize: 11, letterSpacing: 1.5 }}>
          FILTERED OUT
        </div>
      </div>

      {/* ── 3D CSS + keyframes ── */}
      <style>{`
        .mi-3d-stage { transform-style: preserve-3d; }
        .mi-card-deck {
          padding: 8px 10px;
          border-radius: 6px;
          background: linear-gradient(135deg, rgba(15,30,61,0.96), rgba(7,17,33,0.96));
          border: 1.5px solid rgba(34,211,238,0.55);
          box-shadow: 0 8px 24px rgba(0,0,0,0.5), 0 0 18px rgba(34,211,238,0.25);
          backface-visibility: hidden;
        }
        /* Generic in-flight chain card — BIGGER per owner's instruction. */
        .mi-chain-card {
          position: absolute;
          left: 0; top: 0;
          width: 210px; height: 118px;
          padding: 10px 13px;
          border-radius: 9px;
          background: linear-gradient(135deg, rgba(15,30,61,0.96), rgba(7,17,33,0.96));
          border: 1.8px solid #22d3ee;
          box-shadow: 0 0 16px rgba(34,211,238,0.4);
          will-change: transform, opacity;
          pointer-events: none;
          transform-style: preserve-3d;
          font-size: 13px;
          z-index: 18;
          animation-iteration-count: infinite;
          animation-timing-function: linear;
          opacity: 0;
        }
        .mi-chain-in       { animation-name: mi-chain-in;       animation-duration: 14s; z-index: 6; }
        .mi-chain-approved { animation-name: mi-chain-approved; animation-duration: 9s;  z-index: 20; }
        .mi-chain-reject   { animation-name: mi-chain-reject;   animation-duration: 7s;  z-index: 16; }

        /* BACK suction smoke — cyan blur blobs that scale up + drift toward
           the matrix back-left, then fade. Sits BEHIND the matrix (z 7). */
        .mi-suction-smoke {
          position: absolute;
          width: 220px;
          height: 90px;
          border-radius: 50%;
          background: radial-gradient(ellipse at center,
            rgba(34,211,238,0.55) 0%,
            rgba(34,211,238,0.25) 35%,
            rgba(34,211,238,0) 75%);
          filter: blur(14px);
          pointer-events: none;
          z-index: 7;
          opacity: 0;
          animation: mi-suction 2.8s ease-in infinite;
          mix-blend-mode: screen;
        }
        @keyframes mi-suction {
          0%   { transform: translate(0px, 0px) scale(0.6) rotate(-8deg); opacity: 0; }
          20%  { opacity: 0.9; }
          70%  { transform: translate(360px, 240px) scale(1.3) rotate(-12deg); opacity: 0.7; }
          100% { transform: translate(460px, 290px) scale(0.4) rotate(-16deg); opacity: 0; }
        }

        /* FRONT blow-out strobe — green flash on the matrix front-right
           edge, fires every ~1.8s like the auto-trader placing a trade. */
        .mi-front-blowout {
          position: absolute;
          /* Anchored to the matrix front-right exit point in the stage. */
          left: 660px; top: 320px;
          width: 200px; height: 160px;
          border-radius: 50%;
          background: radial-gradient(ellipse at 35% 50%,
            rgba(16,185,129,0.7) 0%,
            rgba(16,185,129,0.35) 30%,
            rgba(16,185,129,0) 65%);
          filter: blur(8px);
          pointer-events: none;
          z-index: 15;
          opacity: 0;
          animation: mi-blowout 1.8s ease-out infinite;
          mix-blend-mode: screen;
        }
        @keyframes mi-blowout {
          0%   { transform: scale(0.6); opacity: 0; }
          18%  { transform: scale(1.05); opacity: 1; filter: blur(8px) drop-shadow(0 0 20px #10b981); }
          30%  { transform: scale(1.15); opacity: 0.85; }
          55%  { transform: scale(1.0);  opacity: 0.4; }
          100% { transform: scale(0.6);  opacity: 0; }
        }

        /* INPUT — TIGHT chain from upper-LEFT to matrix BACK.
           CRITICAL: translateZ stays NEGATIVE the entire path so the
           cards are ALWAYS BEHIND the matrix front plate (at z=0) in 3D
           space. Size growth is done via scale() only. When a card's
           x,y enters the matrix area, the opaque front plate hides it. */
        @keyframes mi-chain-in {
          /* Owner Feb 2026 v2: "the cards coming into the AI filter matrix
             need to go down into the bin — all of them are coming in,
             should be going back out into the bin obviously a lot smaller
             only fit into the bin". Animation lengthened: cards still
             dive in to the matrix entry point (0%→68%), but then EJECT
             out the front-left, arc down, and drop into the bin (at
             stage y≈620, x≈605). Smaller scale through the bin path so
             they "fit". This replaces the old vanish-into-matrix
             ending. If the bot ever fires a real approved trade the
             chain class will get swapped at render time to
             mi-chain-in-approved (see APPROVED_CHAIN below). */
          0%   { transform: translate(-60px, 80px)   translateZ(-40px) rotate(-12deg) scale(0.3);  opacity: 0; }
          8%   { transform: translate(-20px, 110px)  translateZ(-40px) rotate(-10deg) scale(0.42); opacity: 1; }
          25%  { transform: translate(90px, 170px)   translateZ(-40px) rotate(-8deg)  scale(0.6);  opacity: 1; }
          48%  { transform: translate(220px, 240px)  translateZ(-40px) rotate(-4deg)  scale(0.85); opacity: 1; }
          68%  { transform: translate(350px, 310px)  translateZ(-40px) rotate(-2deg)  scale(0.95); opacity: 1; }
          /* now inside the matrix — bounce + change direction */
          74%  { transform: translate(430px, 350px)  translateZ(-60px) rotate(0deg)   scale(0.85); opacity: 1;
                 filter: drop-shadow(0 0 12px #ef4444); }
          /* ejecting down-left towards the bin (bin at ~ x=605, y=620) */
          82%  { transform: translate(560px, 430px)  translateZ(-50px) rotate(-6deg)  scale(0.55); opacity: 0.95;
                 filter: drop-shadow(0 0 10px #ef4444); }
          90%  { transform: translate(595px, 530px)  translateZ(-60px) rotate(-12deg) scale(0.32); opacity: 0.85; }
          95%  { transform: translate(605px, 595px)  translateZ(-80px) rotate(-15deg) scale(0.18); opacity: 0.45; }
          100% { transform: translate(607px, 660px)  translateZ(-100px) rotate(-17deg) scale(0.06); opacity: 0; }
        }
        /* APPROVED — starts TINY deep inside the matrix back, then comes
           FORWARD (towards camera) from the MIDDLE of the matrix box —
           popping ~180px out the FRONT face like a card being thrown at
           you — before curving UP-RIGHT into the Buy Signals folder.
           Owner brief (Feb 2026): "the ones coming out the front that
           approved are coming out of the middle of the AI matrix box,
           basically moving forward like 2 inch on the screen". */
        @keyframes mi-chain-approved {
          0%   { transform: translate(685px, 395px) translateZ(-220px) rotate(0deg)   scale(0.18); opacity: 0; }
          12%  { transform: translate(685px, 395px) translateZ(-110px) rotate(0deg)   scale(0.40); opacity: 0.85; }
          30%  { transform: translate(690px, 390px) translateZ(0px)    rotate(0deg)   scale(0.78); opacity: 1; }
          50%  { transform: translate(700px, 380px) translateZ(180px)  rotate(-2deg)  scale(1.18); opacity: 1;
                 filter: drop-shadow(0 0 26px #22d3ee) drop-shadow(0 0 12px rgba(34,211,238,0.6)); }
          62%  { transform: translate(760px, 340px) translateZ(160px)  rotate(-6deg)  scale(1.08); opacity: 1;
                 filter: drop-shadow(0 0 20px #22d3ee); }
          80%  { transform: translate(920px, 220px) translateZ(90px)   rotate(-14deg) scale(0.85); opacity: 0.95; }
          92%  { transform: translate(1020px, 120px) translateZ(40px)  rotate(-20deg) scale(0.55); opacity: 0.7; }
          100% { transform: translate(1075px, 70px) translateZ(20px)   rotate(-22deg) scale(0.42); opacity: 0; }
        }
        /* REJECT — bin lowered to top:570 so card has more vertical room
           to shrink gradually before entering rim (now at stage y≈604).
           Path lengthened, scale shrinks slower for a longer visible fall. */
        @keyframes mi-chain-reject {
          0%   { transform: translate(555px, 365px) translateZ(-30px) rotate(0deg)   scale(0.85); opacity: 0; }
          10%  { transform: translate(570px, 400px) translateZ(-30px) rotate(-2deg)  scale(0.78); opacity: 1;
                 filter: drop-shadow(0 0 14px #ef4444); }
          28%  { transform: translate(585px, 460px) translateZ(-40px) rotate(-5deg)  scale(0.6);  opacity: 1; }
          48%  { transform: translate(595px, 520px) translateZ(-55px) rotate(-8deg)  scale(0.42); opacity: 1; }
          65%  { transform: translate(600px, 580px) translateZ(-70px) rotate(-10deg) scale(0.28); opacity: 0.95; }
          76%  { transform: translate(603px, 610px) translateZ(-90px) rotate(-12deg) scale(0.18); opacity: 0.45; }
          85%  { transform: translate(605px, 635px) translateZ(-110px) rotate(-13deg) scale(0.1);  opacity: 0.08; }
          92%  { transform: translate(605px, 655px) translateZ(-125px) rotate(-13deg) scale(0.06); opacity: 0; }
          100% { transform: translate(605px, 680px) translateZ(-140px) rotate(-14deg) scale(0.03); opacity: 0; }
        }
        @keyframes mi-pulse {
          0%,100% { stroke-opacity: 0.55; }
          50%     { stroke-opacity: 1; filter: drop-shadow(0 0 12px ${T.cyan}); }
        }
        @keyframes mi-blink { 0%,100% { opacity: 0.4; } 50% { opacity: 1; } }
        @keyframes mi-spin  { from { transform: rotate(0); } to { transform: rotate(360deg); } }
        @keyframes mi-winner-glow {
          0%,100% {
            box-shadow:
              0 0 18px #22d3ee,
              0 0 40px rgba(34,211,238,0.55),
              0 0 80px rgba(34,211,238,0.25),
              inset 0 0 18px rgba(34,211,238,0.15);
          }
          50% {
            box-shadow:
              0 0 28px #22d3ee,
              0 0 60px rgba(34,211,238,0.75),
              0 0 120px rgba(34,211,238,0.4),
              inset 0 0 24px rgba(34,211,238,0.22);
          }
        }
        @keyframes mi-new-badge {
          0%,100% { opacity: 1; transform: scale(1); }
          50%     { opacity: 0.7; transform: scale(1.08); }
        }

        /* ═══ AI FILTERING CORE — pulsating electronic brain ═══
           Sits inside the matrix box. All layers are pointer-events:none.

           Owner Feb 2026: "the app is running very slow". The matrix
           animations were running continuously even off-screen. Wrap
           the whole core in a reduced-motion respecter so slow phones
           AND users who tick "reduce motion" in iOS settings get a
           static-but-still-visible matrix instead of 6 stacked
           animations chewing 60fps GPU. */
        @media (prefers-reduced-motion: reduce) {
          .mi-ai-core-pulse,
          .mi-ai-core-ring,
          .mi-ai-core-ring-inner,
          .mi-ai-core-grid > span,
          .mi-ai-core-scanline,
          .mi-ai-core-rain,
          .mi-ai-core-node,
          .mi-ai-core-label { animation: none !important; }
          .mi-ai-core-rain { opacity: 0.4; }
          .mi-ai-core-pulse { opacity: 0.6; }
        }
        @keyframes mi-ai-pulse {
          0%,100% {
            opacity: 0.35;
            transform: translate(-50%,-50%) scale(1);
            box-shadow: 0 0 22px 4px rgba(34,211,238,0.55),
                        0 0 50px 10px rgba(34,211,238,0.22),
                        inset 0 0 18px rgba(34,211,238,0.35);
          }
          50% {
            opacity: 0.95;
            transform: translate(-50%,-50%) scale(1.18);
            box-shadow: 0 0 36px 8px rgba(34,211,238,0.85),
                        0 0 80px 18px rgba(34,211,238,0.45),
                        inset 0 0 28px rgba(34,211,238,0.5);
          }
        }
        @keyframes mi-ai-ring-spin   { from { transform: translate(-50%,-50%) rotate(0); }  to { transform: translate(-50%,-50%) rotate(360deg);  } }
        @keyframes mi-ai-ring-spin-r { from { transform: translate(-50%,-50%) rotate(0); }  to { transform: translate(-50%,-50%) rotate(-360deg); } }
        @keyframes mi-ai-grid-flicker {
          0%, 30%, 100% { opacity: 0.18; box-shadow: 0 0 2px rgba(34,211,238,0.35); }
          14%, 18%      { opacity: 1;    box-shadow: 0 0 10px rgba(34,211,238,1), 0 0 16px rgba(34,211,238,0.6); }
        }
        @keyframes mi-ai-scanline {
          0%   { top: -8%;  opacity: 0;   }
          12%  { opacity: 0.9; }
          80%  { opacity: 0.9; }
          100% { top: 108%; opacity: 0;  }
        }
        @keyframes mi-ai-rain {
          0%   { transform: translateY(-100%); opacity: 0; }
          12%  { opacity: 0.55; }
          88%  { opacity: 0.55; }
          100% { transform: translateY(110%);  opacity: 0; }
        }
        @keyframes mi-ai-node-throb {
          0%,100% { transform: translate(-50%,-50%) scale(1);    box-shadow: 0 0 14px 2px rgba(34,211,238,1), 0 0 28px 6px rgba(34,211,238,0.55); }
          50%     { transform: translate(-50%,-50%) scale(1.45); box-shadow: 0 0 22px 4px rgba(255,255,255,1), 0 0 44px 10px rgba(34,211,238,0.85); }
        }
        @keyframes mi-ai-label {
          0%,100% { opacity: 0.45; letter-spacing: 0.18em; }
          50%     { opacity: 1;    letter-spacing: 0.28em; }
        }

        .mi-ai-core-pulse {
          position: absolute; left: 50%; top: 50%;
          width: 96px; height: 96px; border-radius: 50%;
          background: radial-gradient(circle, rgba(34,211,238,0.55) 0%, rgba(34,211,238,0.18) 45%, rgba(7,17,33,0) 75%);
          animation: mi-ai-pulse 2.4s ease-in-out infinite;
        }
        .mi-ai-core-ring {
          position: absolute; left: 50%; top: 50%;
          width: 180px; height: 180px; border-radius: 50%;
          border: 1.5px dashed rgba(34,211,238,0.85);
          border-right-color: rgba(34,211,238,0.25);
          border-bottom-color: rgba(34,211,238,0.25);
          animation: mi-ai-ring-spin 8s linear infinite;
          filter: drop-shadow(0 0 6px rgba(34,211,238,0.65));
        }
        .mi-ai-core-ring-inner {
          position: absolute; left: 50%; top: 50%;
          width: 130px; height: 130px; border-radius: 50%;
          border: 1px solid rgba(34,211,238,0.6);
          border-left-color: rgba(34,211,238,0.15);
          border-top-color:  rgba(34,211,238,0.15);
          animation: mi-ai-ring-spin-r 5s linear infinite;
        }
        .mi-ai-core-grid {
          position: absolute; inset: 18px;
          display: grid; grid-template-columns: repeat(6, 1fr); grid-template-rows: repeat(6, 1fr);
          gap: 12px;
          pointer-events: none;
        }
        .mi-ai-core-grid > span {
          display: block; width: 100%; height: 100%;
          border-radius: 50%;
          background: rgba(34,211,238,0.85);
          animation: mi-ai-grid-flicker 2.8s ease-in-out infinite;
        }
        .mi-ai-core-scanline {
          position: absolute; left: 0; right: 0;
          height: 24px;
          background: linear-gradient(180deg,
            transparent 0%,
            rgba(34,211,238,0.18) 35%,
            rgba(255,255,255,0.45) 50%,
            rgba(34,211,238,0.18) 65%,
            transparent 100%);
          box-shadow: 0 0 18px rgba(34,211,238,0.65);
          animation: mi-ai-scanline 3.6s linear infinite;
        }
        .mi-ai-core-rain {
          position: absolute;
          font-family: ui-monospace, "JetBrains Mono", "SF Mono", monospace;
          font-size: 9px; line-height: 12px;
          color: rgba(34,211,238,0.85);
          text-shadow: 0 0 6px rgba(34,211,238,0.85);
          animation: mi-ai-rain 5.2s linear infinite;
        }
        .mi-ai-core-rain--a { left: 14px; top: 0; animation-delay: 0s;   }
        .mi-ai-core-rain--b { right: 14px; top: 0; animation-delay: -2.6s; animation-duration: 6.4s; }
        .mi-ai-core-node {
          position: absolute; left: 50%; top: 50%;
          width: 14px; height: 14px; border-radius: 50%;
          background: radial-gradient(circle, #ffffff 0%, #22d3ee 55%, rgba(34,211,238,0) 100%);
          animation: mi-ai-node-throb 1.4s ease-in-out infinite;
        }
        .mi-ai-core-cross {
          position: absolute; left: 50%; top: 50%;
          width: 220px; height: 220px;
          transform: translate(-50%,-50%);
          background:
            linear-gradient(to right,  transparent calc(50% - 0.5px), rgba(34,211,238,0.18) calc(50% - 0.5px), rgba(34,211,238,0.18) calc(50% + 0.5px), transparent calc(50% + 0.5px)),
            linear-gradient(to bottom, transparent calc(50% - 0.5px), rgba(34,211,238,0.18) calc(50% - 0.5px), rgba(34,211,238,0.18) calc(50% + 0.5px), transparent calc(50% + 0.5px));
        }
        .mi-ai-core-bracket {
          position: absolute; width: 16px; height: 16px;
          border: 1.5px solid rgba(34,211,238,0.9);
          filter: drop-shadow(0 0 4px rgba(34,211,238,0.7));
        }
        .mi-ai-core-bracket--tl { top: 6px;    left: 6px;    border-right: 0; border-bottom: 0; }
        .mi-ai-core-bracket--tr { top: 6px;    right: 6px;   border-left: 0;  border-bottom: 0; }
        .mi-ai-core-bracket--bl { bottom: 6px; left: 6px;    border-right: 0; border-top: 0;    }
        .mi-ai-core-bracket--br { bottom: 6px; right: 6px;   border-left: 0;  border-top: 0;    }
        .mi-ai-core-label {
          position: absolute; left: 50%; bottom: 14px;
          transform: translateX(-50%);
          font-family: ui-monospace, "JetBrains Mono", "SF Mono", monospace;
          font-size: 9px;
          color: rgba(255,255,255,0.9);
          text-shadow: 0 0 8px rgba(34,211,238,0.9);
          animation: mi-ai-label 2.4s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}

/* ───── One-tap-trade modal ───── */
function TradeModal({ open, symbol, suggested, onClose, onPlaced }) {
  const [capital, setCapital] = useState(suggested?.capital || 100);
  const [side, setSide] = useState(suggested?.side || "buy");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (open) { setErr(""); setDone(null); setBusy(false); setCapital(suggested?.capital || 100); setSide(suggested?.side || "buy"); }
  }, [open, suggested]);

  if (!open) return null;
  const place = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.post("/trades", { symbol, side, capital: parseFloat(capital) });
      setDone(r.data);
      onPlaced?.(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Trade failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center"
         style={{ background: "rgba(7,17,33,0.85)" }}
         onClick={onClose}
         data-testid="trade-modal">
      <div className="rounded-md p-5 w-[360px]"
           style={{ background: T.bg2, border: `1px solid ${T.cyan}`, boxShadow: `0 0 40px rgba(34,211,238,0.3)` }}
           onClick={(e) => e.stopPropagation()}>
        <div className="text-[13px] font-semibold mb-3" style={{ color: T.cyan }}>
          One-Tap Trade · <span style={{ color: T.text }}>{symbol}</span>
        </div>
        {!done && (
          <>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <button onClick={() => setSide("buy")}
                      className="py-2 text-[11px] font-semibold rounded"
                      style={{
                        background: side === "buy" ? T.greenSoft : "transparent",
                        color: side === "buy" ? T.green : T.textDim,
                        border: `1px solid ${side === "buy" ? T.green : T.border}`,
                      }} data-testid="trade-side-buy">BUY ↑</button>
              <button onClick={() => setSide("sell")}
                      className="py-2 text-[11px] font-semibold rounded"
                      style={{
                        background: side === "sell" ? T.redSoft : "transparent",
                        color: side === "sell" ? T.red : T.textDim,
                        border: `1px solid ${side === "sell" ? T.red : T.border}`,
                      }} data-testid="trade-side-sell">SELL ↓</button>
            </div>
            <label className="text-[10px]" style={{ color: T.textDim }}>Capital (£)</label>
            <input
              type="number" value={capital} onChange={(e) => setCapital(e.target.value)}
              className="w-full text-[14px] px-2 py-1.5 mt-1 mb-3 rounded"
              style={{ background: T.bg, color: T.text, border: `1px solid ${T.border}` }}
              data-testid="trade-capital-input"
            />
            <div className="text-[9px] mb-3" style={{ color: T.textDim }}>
              Auto 50-pip stop-loss applied. Routes through Capital.com (live broker).
            </div>
            {err && <div className="text-[10px] mb-2" style={{ color: T.red }}>{err}</div>}
            <div className="flex gap-2">
              <button onClick={onClose} className="flex-1 py-2 text-[11px] rounded"
                      style={{ color: T.textDim, border: `1px solid ${T.border}` }}>Cancel</button>
              <button onClick={place} disabled={busy}
                      className="flex-1 py-2 text-[11px] font-semibold rounded"
                      style={{
                        background: side === "buy" ? T.green : T.red,
                        color: T.bg,
                        opacity: busy ? 0.6 : 1,
                      }} data-testid="trade-confirm-btn">
                {busy ? "PLACING…" : `CONFIRM ${side.toUpperCase()}`}
              </button>
            </div>
          </>
        )}
        {done && (
          <>
            <div className="text-[11px] mb-2" style={{ color: T.green }}>✓ Trade placed</div>
            <div className="text-[10px] space-y-1" style={{ color: T.text }}>
              <div>Symbol: <b>{done.symbol}</b></div>
              <div>Side: <b>{done.side?.toUpperCase()}</b></div>
              <div>Entry: <b>${done.entry_price?.toFixed?.(2) ?? done.entry_price}</b></div>
              <div>Broker: <b>{done.broker}</b></div>
              {done.broker_order_id && <div>Order ID: <b>{done.broker_order_id}</b></div>}
            </div>
            <button onClick={onClose} className="w-full mt-3 py-2 text-[11px] rounded"
                    style={{ background: T.cyan, color: T.bg }} data-testid="trade-close-btn">Done</button>
          </>
        )}
      </div>
    </div>
  );
}


function BuyOppsCompact({ rows, onTrade }) {
  const data = (rows && rows.length ? rows : FALLBACK_BUY).slice(0, 3);
  return (
    <Panel title="Buy Opportunities" testid="trading-buy-opps">
      <table className="w-full text-[10px]">
        <thead>
          <tr style={{ color: T.textDim }}>
            <th className="text-left py-1 font-normal">Stock</th>
            <th className="text-right font-normal">Price</th>
            <th className="text-right font-normal">% Change</th>
            <th className="text-right font-normal">Rationale</th>
          </tr>
        </thead>
        <tbody>
          {data.map((b) => {
            const up = (b.change_pct ?? 0) >= 0;
            return (
              <tr key={b.symbol} style={{ borderTop: `1px solid ${T.border}` }}>
                <td className="py-1.5">
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold"
                         style={{ background: T.cyan, color: T.bg }}>
                      {b.symbol[0]}
                    </div>
                    <div>
                      <div className="font-semibold" style={{ color: T.text }}>{b.symbol}</div>
                      <div className="text-[8px]" style={{ color: T.textDim }}>{b.rationale || "AI Sector Strength"}</div>
                    </div>
                  </div>
                </td>
                <td className="text-right" style={{ color: T.text }}>${(b.price ?? 0).toFixed(2)}</td>
                <td className="text-right" style={{ color: up ? T.green : T.red }}>
                  {up ? "+" : ""}{(b.change_pct ?? 0).toFixed(2)}%
                </td>
                <td className="text-right">
                  <button
                    onClick={() => onTrade?.({ symbol: b.symbol, side: up ? "buy" : "sell" })}
                    className="inline-block px-1.5 py-0.5 text-[8px] rounded font-semibold cursor-pointer hover:brightness-125"
                    style={{ background: T.greenSoft, color: T.green, border: `1px solid ${T.green}` }}
                    data-testid={`buy-signal-${b.symbol}`}>
                    BUY SIGNAL ⚡
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Panel>
  );
}

/* ───── Top-right Market Trends (4 mini candles) ───── */
function MarketTrendsTop() {
  return (
    <Panel title="Market Trends" testid="trading-market-trends-top">
      <div className="grid grid-cols-2 gap-2">
        {TREND_INDICES.map((idx, i) => (
          <CandleChart key={i} seed={i * 13 + 5} name={idx.name} change={idx.change} pct={idx.pct} height={56}/>
        ))}
      </div>
    </Panel>
  );
}

/* ───── Sector heatmap (3×6) ───── */
function SectorHeatmap() {
  // Map: positives = green, negatives = red — mirror screenshot mix.
  const PATTERN = [
    [1, -1,  1,  -1, -1, -1],
    [-1,  1, -1, -1, -1,  1],
    [1,  1,  1,  1,  -1,  -1],
  ];
  return (
    <Panel title="Sector" right={<span className="text-[10px]" style={{ color: T.green }}>+3 ↑</span>} testid="trading-sector">
      <div className="grid grid-cols-6 gap-0.5">
        {SECTOR_GRID.flatMap((row, ri) => row.map((s, ci) => {
          const pos = PATTERN[ri][ci] > 0;
          return (
            <div key={`${ri}-${ci}`}
                 className="text-center py-2.5 text-[9px] font-bold rounded-sm"
                 style={{
                   background: pos ? T.greenSoft : T.redSoft,
                   color: pos ? T.green : T.red,
                   border: `1px solid ${pos ? "rgba(16,185,129,0.4)" : "rgba(239,68,68,0.4)"}`,
                 }}>
              {s}
            </div>
          );
        }))}
      </div>
    </Panel>
  );
}

/* ───── Portfolio Risk panel (right column) ───── */
function PortfolioRiskTop() {
  const rows = [
    { label: "Overall Risk", color: T.cyan,  val: "$0" },
    { label: "Portfolio Risk", color: T.green, val: "$0" },
    { label: "Beverly Risk",   color: T.amber, val: "$0" },
    { label: "Portfolio",      color: T.red,   val: "$0" },
  ];
  return (
    <Panel title="Portfolio Risk" testid="trading-portfolio-risk-top">
      <div className="flex items-center gap-3">
        <div className="flex-1 space-y-1">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center justify-between text-[10px]">
              <span className="flex items-center gap-1.5" style={{ color: T.textDim }}>
                <span className="w-2 h-2 rounded-sm" style={{ background: r.color }}/>
                {r.label}
              </span>
              <span style={{ color: T.text }}>{r.val}</span>
            </div>
          ))}
        </div>
        <RiskGauge value={0.58} size={100}/>
      </div>
    </Panel>
  );
}

/* ───── BOTTOM ROW: Buy Opportunities w/ sparklines ───── */
function BuyOppsDetail({ rows, onTrade }) {
  const data = (rows && rows.length ? rows : FALLBACK_BUY).slice(0, 5);
  return (
    <Panel title="Buy Opportunities"
           right={<span className="text-[9px] flex items-center gap-2" style={{ color: T.textDim }}>STOCKS RATIONALE</span>}
           testid="trading-buy-opps-detail">
      <div className="space-y-2">
        {data.map((b, i) => {
          const up = (b.change_pct ?? 0) >= 0;
          return (
            <div key={`${b.symbol}-${i}`} className="flex items-center gap-2 py-1.5"
                 style={{ borderBottom: i < data.length - 1 ? `1px solid ${T.border}` : "none" }}>
              <div className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold"
                   style={{ background: T.cyan, color: T.bg }}>
                {b.symbol[0]}
              </div>
              <div className="flex-1">
                <div className="text-[11px] font-semibold" style={{ color: T.text }}>{b.symbol}</div>
                <div className="text-[8px]" style={{ color: T.textDim }}>{b.rationale || "AI Sector Strength"}</div>
              </div>
              <Sparkline seed={symSeed(b.symbol)} up={up} width={60} height={22}/>
              <div className="text-right" style={{ minWidth: 56 }}>
                <div className="text-[10px]" style={{ color: T.text }}>${(b.price ?? 0).toFixed(2)}</div>
                <div className="text-[9px]" style={{ color: up ? T.green : T.red }}>
                  {up ? "+" : ""}{(b.change_pct ?? 0).toFixed(2)}%
                </div>
              </div>
              <button
                onClick={() => onTrade?.({ symbol: b.symbol, side: up ? "buy" : "sell" })}
                className="text-[8px] px-1.5 py-0.5 rounded cursor-pointer hover:brightness-125 font-semibold"
                style={{ background: T.greenSoft, color: T.green, border: `1px solid ${T.green}` }}
                data-testid={`buy-signal-detail-${b.symbol}`}>
                BUY SIGNAL ⚡
              </button>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}

/* ───── BOTTOM ROW: Market Trends w/ 4 larger candles ───── */
function MarketTrendsBottom() {
  return (
    <Panel title="Market Trends" testid="trading-market-trends-bottom">
      <div className="grid grid-cols-2 gap-2">
        <CandleChart seed={11} name="S&P 500" change="+6.2%" pct={6.2} height={72}/>
        <CandleChart seed={21} name="NASDAQ"  change="-3.2%" pct={-3.2} height={72}/>
        <CandleChart seed={31} name="NASDAQ"  change="-6.3%" pct={-6.3} height={72}/>
        <CandleChart seed={41} name="S&P RN"  change="+3.5%" pct={3.5} height={72}/>
      </div>
    </Panel>
  );
}

/* ───── BOTTOM ROW: Risk Scores (Recharts radars + half-circle gauge) ───── */
function ReRadar({ data, title }) {
  return (
    <div className="flex flex-col items-center">
      <div style={{ width: "100%", height: 130 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ReRadarChart data={data} outerRadius="78%">
            <PolarGrid stroke={T.border} strokeOpacity={0.7}/>
            <PolarAngleAxis dataKey="axis"
              tick={{ fill: T.textDim, fontSize: 8 }}
              tickLine={false}
            />
            <Radar
              dataKey="value"
              stroke={T.cyan}
              fill={T.cyan}
              fillOpacity={0.28}
              strokeWidth={1.4}
              dot={{ fill: T.cyan, r: 2 }}
            />
          </ReRadarChart>
        </ResponsiveContainer>
      </div>
      {title && <div className="text-[10px] mt-0.5" style={{ color: T.textDim }}>{title}</div>}
    </div>
  );
}

function HalfGauge({ value = 0.5, label = "RISK" }) {
  // value 0..1. Pie split into 3 equal arcs (green→amber→red) over a 180° span.
  // Needle uses a separate Pie with two slices: filled = value*180°, rest invisible.
  const ARC = [
    { v: 1, c: T.green },
    { v: 1, c: T.amber },
    { v: 1, c: T.red   },
  ];
  const needleAngle = 180 - value * 180; // recharts: 0° = right, sweeps counter-clockwise
  return (
    <div className="relative" style={{ width: 130, height: 95 }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          {/* Coloured arc track */}
          <Pie
            data={ARC}
            dataKey="v"
            cx="50%" cy="92%"
            startAngle={180} endAngle={0}
            innerRadius={48} outerRadius={66}
            stroke="none"
            isAnimationActive={false}
          >
            {ARC.map((a, i) => <Cell key={i} fill={a.c}/>)}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      {/* Needle overlay (pure SVG so we control angle precisely) */}
      <svg viewBox="0 0 130 95" className="absolute inset-0 w-full h-full pointer-events-none">
        <g transform={`translate(65,87) rotate(${-90 + value * 180})`}>
          <line x1="0" y1="0" x2="0" y2="-54" stroke="#ffffff" strokeWidth="2" strokeLinecap="round"/>
          <circle cx="0" cy="0" r="4" fill="#ffffff"/>
        </g>
        <text x="8"   y="92" fontSize="8" fill={T.textDim}>Low</text>
        <text x="65"  y="92" fontSize="8" textAnchor="middle" fill={T.text}>{label}</text>
        <text x="122" y="92" fontSize="8" textAnchor="end" fill={T.textDim}>High</text>
      </svg>
      <span className="sr-only" data-testid="risk-gauge-value">{Math.round(value * 100)}</span>
    </div>
  );
}

function RiskScoresPanel() {
  const volatility = [
    { axis: "Tris",     value: 0.65 },
    { axis: "Br",       value: 0.50 },
    { axis: "Lbx",      value: 0.80 },
    { axis: "Stoskcs",  value: 0.42 },
    { axis: "Qt",       value: 0.58 },
  ];
  const leverage = [
    { axis: "BK",       value: 0.55 },
    { axis: "DDcs",     value: 0.72 },
    { axis: "ET",       value: 0.45 },
    { axis: "Risk",     value: 0.60 },
    { axis: "UV",       value: 0.38 },
  ];
  const matrix = [
    { label: "Volatility", vals: [5, 4, 7] },
    { label: "Leverage",   vals: [2, 3, 4] },
    { label: "Low",        vals: [1, 3, 3] },
    { label: "Low",        vals: [1, 2, 2] },
  ];
  const cellColor = (v) => v >= 5 ? T.red : v >= 3 ? T.amber : T.green;
  return (
    <Panel title="Risk Scores" testid="trading-risk-scores">
      <div className="flex gap-3">
        <div className="flex-1">
          <table className="w-full text-[9px]">
            <thead>
              <tr style={{ color: T.textDim }}>
                <th></th>
                <th className="text-center font-normal">Low</th>
                <th className="text-center font-normal">Med</th>
                <th className="text-center font-normal">High</th>
              </tr>
            </thead>
            <tbody>
              {matrix.map((r, i) => (
                <tr key={i}>
                  <td className="text-[9px] py-0.5 pr-1" style={{ color: T.text }}>{r.label}</td>
                  {r.vals.map((v, j) => (
                    <td key={j} className="text-center p-0.5">
                      <div className="rounded-sm font-bold text-[9px] py-0.5"
                           style={{ background: cellColor(v), color: "#0a1628" }}>{v}</div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-col items-center" style={{ width: 130 }}>
          <div className="text-[9px] mb-1" style={{ color: T.textDim }}>Portfolio Risk</div>
          <HalfGauge value={0.5} label="RISK"/>
          <div className="text-[9px]" style={{ color: T.text }}>Overall</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-2">
        <ReRadar data={volatility} title="Volatility"/>
        <ReRadar data={leverage}   title="Leverage"/>
      </div>
    </Panel>
  );
}

/* ───── BOTTOM ROW: Smart Watchlists — owner brief Feb 2026: "My smart
   watchlist needs to show all my ones on my watchlist that the capital
   watchlist that are on the screenshots". Live from
   /api/trading/capital_watchlist; falls back to the static WATCHLIST. ───── */
function SmartWatchlistsPanel() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    let alive = true;
    const fetch = () => api.get("/trading/capital_watchlist").then(r => {
      if (alive && Array.isArray(r.data?.items) && r.data.items.length) {
        setItems(r.data.items.map((it) => ({
          sym: it.symbol,
          group: (it.change_pct ?? 0) >= 0 ? "Trending Up" : "Trending Down",
          pct: it.change_pct ?? 0,
          sub: `Capital live · mid ${(it.price ?? 0).toFixed(2)}`,
        })));
      }
    }).catch(() => {});
    fetch();
    const id = setInterval(fetch, 6000);
    return () => { alive = false; clearInterval(id); };
  }, []);
  const list = items.length ? items : WATCHLIST;
  return (
    <Panel title={`Smart Watchlists · ${list.length}`} testid="trading-watchlists">
      <select
        className="w-full text-[10px] px-2 py-1 mb-2 rounded"
        style={{ background: "rgba(7,17,33,0.7)", color: T.text, border: `1px solid ${T.border}` }}
      >
        <option>Capital watchlist (live)</option>
      </select>
      <div className="max-h-[280px] overflow-y-auto pr-1">
        {list.map((it) => {
          const up = it.pct >= 0;
          return (
            <div key={it.sym} className="py-2" style={{ borderBottom: `1px solid ${T.border}` }} data-testid={`watchlist-${it.sym}`}>
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold"
                     style={{ background: T.cyan, color: T.bg }}>{it.sym[0]}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-semibold truncate" style={{ color: T.text }}>{it.sym}</div>
                  <div className="text-[8px]" style={{ color: T.cyan }}>{it.group}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px]" style={{ color: up ? T.green : T.red }}>{up ? "+" : ""}{it.pct.toFixed(2)}%</div>
                </div>
                <Sparkline seed={symSeed(it.sym) + 3} up={up} width={40} height={20}/>
              </div>
              <div className="text-[8px] mt-1" style={{ color: T.textDim }}>{it.sub}</div>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}

/* ───── Bottom status bar ───── */
function BottomBar() {
  return (
    <div
      className="flex items-center gap-4 px-4 py-1.5 text-[10px]"
      style={{ background: "rgba(7,17,33,0.85)", borderTop: `1px solid ${T.border}`, color: T.textDim }}
    >
      <span className="px-2 py-0.5 rounded" style={{ background: "rgba(34,211,238,0.12)", color: T.cyan }}>Market Cap &gt;</span>
      <span className="flex-1 truncate">
        <span style={{ color: T.cyan }}>News Ticker </span>
        financial right today's people-renominations  ·  8:18.18  <span style={{ color: T.red }}>-3.93%</span>
        <span className="mx-3" style={{ color: T.cyan }}>News Ticker </span>
        few US providers to overloading communicates · 8:31.23 <span style={{ color: T.green }}>+0.83%</span>
      </span>
      <span style={{ color: T.text }}>Robotraider RISK Dashboard · 00</span>
    </div>
  );
}

/* ───── Auto-Trade Control Panel ───── */
function AutoTradePanel() {
  const [s, setS] = useState(null);
  const [logRows, setLogRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);

  const reload = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([
        api.get("/trading/auto_settings").then(r => r.data),
        api.get("/trading/auto_log?limit=20").then(r => r.data),
      ]);
      setS(a);
      setLogRows(b.entries || []);
      if (!editing) setForm(a);
    } catch (_) { /* ignore */ }
  }, [editing]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    const id = setInterval(reload, 15000);
    return () => clearInterval(id);
  }, [reload]);

  const flip = async () => {
    setBusy(true);
    try {
      await api.post(s?.armed ? "/trading/auto_stop" : "/trading/auto_start");
      await reload();
    } finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/trading/auto_settings", {
        per_trade_gbp: parseFloat(form.per_trade_gbp),
        daily_cap_gbp: parseFloat(form.daily_cap_gbp),
        max_open_positions: parseInt(form.max_open_positions, 10),
        min_confidence_pct: parseFloat(form.min_confidence_pct),
        cooldown_minutes: parseInt(form.cooldown_minutes, 10),
        stop_loss_pips: parseInt(form.stop_loss_pips, 10),
        take_profit_pips: parseInt(form.take_profit_pips, 10),
        trailing_stop: !!form.trailing_stop,
        voice_alerts: !!form.voice_alerts,
      });
      setEditing(false);
      await reload();
    } finally { setBusy(false); }
  };

  if (!s) return null;
  const armed = !!s.armed;
  const capPct = Math.min(100, ((s.spent_today_gbp || 0) / (s.daily_cap_gbp || 1)) * 100);

  return (
    <Panel
      title="Auto-Trader · Capital.com"
      testid="auto-trade-panel"
      right={
        <button onClick={flip} disabled={busy}
                className="px-3 py-1 text-[10px] font-semibold rounded"
                style={{
                  background: armed ? T.red : T.green,
                  color: T.bg, opacity: busy ? 0.6 : 1,
                }}
                data-testid="auto-trade-toggle">
          {busy ? "…" : armed ? "● ARMED · STOP" : "○ DISARMED · ARM"}
        </button>
      }
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[10px]">
        <div>
          <div style={{ color: T.textDim }}>Per trade</div>
          <div className="text-[14px]" style={{ color: T.text }}>£{s.per_trade_gbp}</div>
        </div>
        <div>
          <div style={{ color: T.textDim }}>Daily cap · spent</div>
          <div className="text-[14px]" style={{ color: T.text }}>
            £{(s.spent_today_gbp ?? 0).toFixed(0)} <span style={{ color: T.textDim }}>/ £{s.daily_cap_gbp}</span>
          </div>
          <div className="h-1 mt-0.5 rounded" style={{ background: "rgba(255,255,255,0.1)" }}>
            <div className="h-full rounded" style={{ width: `${capPct}%`, background: capPct > 80 ? T.amber : T.cyan }}/>
          </div>
        </div>
        <div>
          <div style={{ color: T.textDim }}>Open / max</div>
          <div className="text-[14px]" style={{ color: T.text }}>{s.open_positions ?? 0} / {s.max_open_positions}</div>
        </div>
        <div>
          <div style={{ color: T.textDim }}>Min signal</div>
          <div className="text-[14px]" style={{ color: T.text }}>{s.min_confidence_pct}%</div>
        </div>
        <div>
          <div style={{ color: T.textDim }}>Stop / TP</div>
          <div className="text-[14px]" style={{ color: T.text }}>{s.stop_loss_pips} / {s.take_profit_pips} pip</div>
        </div>
        <div>
          <div style={{ color: T.textDim }}>Cooldown</div>
          <div className="text-[14px]" style={{ color: T.text }}>{s.cooldown_minutes} min</div>
        </div>
        <div>
          <div style={{ color: T.textDim }}>Trailing stop</div>
          <div className="text-[14px]" style={{ color: s.trailing_stop ? T.cyan : T.textDim }}>
            {s.trailing_stop ? "ON" : "OFF"}
          </div>
        </div>
        <div>
          <div style={{ color: T.textDim }}>Voice alerts</div>
          <div className="text-[14px]" style={{ color: s.voice_alerts ? T.green : T.textDim }}>
            {s.voice_alerts ? "ON" : "OFF"}
          </div>
        </div>
        <div className="flex items-end">
          <button onClick={() => { setEditing(!editing); setForm(s); }}
                  className="text-[10px] px-2 py-1 rounded"
                  style={{ color: T.cyan, border: `1px solid ${T.border}` }}
                  data-testid="auto-trade-edit-btn">
            {editing ? "✕ Cancel" : "✎ Edit"}
          </button>
        </div>
      </div>

      {editing && form && (
        <div className="mt-3 p-3 rounded grid grid-cols-2 md:grid-cols-4 gap-2 text-[10px]"
             style={{ background: "rgba(7,17,33,0.6)", border: `1px solid ${T.border}` }}>
          {[
            ["per_trade_gbp",      "Per trade (£)"],
            ["daily_cap_gbp",      "Daily cap (£)"],
            ["max_open_positions", "Max open"],
            ["min_confidence_pct", "Min signal %"],
            ["stop_loss_pips",     "Stop pips"],
            ["take_profit_pips",   "Take-profit pips"],
            ["cooldown_minutes",   "Cooldown (min)"],
          ].map(([k, lbl]) => (
            <label key={k} className="flex flex-col">
              <span style={{ color: T.textDim }}>{lbl}</span>
              <input
                type="number"
                value={form[k]}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                className="mt-0.5 px-1.5 py-1 rounded"
                style={{ background: T.bg, color: T.text, border: `1px solid ${T.border}` }}
                data-testid={`auto-trade-input-${k}`}
              />
            </label>
          ))}
          <label className="flex items-end gap-1.5">
            <input
              type="checkbox"
              checked={!!form.trailing_stop}
              onChange={(e) => setForm({ ...form, trailing_stop: e.target.checked })}
              data-testid="auto-trade-input-trailing_stop"
            />
            <span style={{ color: T.textDim }}>Trailing stop (lets winners run)</span>
          </label>
          <label className="flex items-end gap-1.5">
            <input
              type="checkbox"
              checked={!!form.voice_alerts}
              onChange={(e) => setForm({ ...form, voice_alerts: e.target.checked })}
              data-testid="auto-trade-input-voice_alerts"
            />
            <span style={{ color: T.textDim }}>Voice alerts</span>
          </label>
          <div className="col-span-2 md:col-span-4 flex justify-end gap-2 mt-1">
            <button onClick={() => setEditing(false)}
                    className="px-3 py-1 rounded"
                    style={{ color: T.textDim, border: `1px solid ${T.border}` }}>Cancel</button>
            <button onClick={save} disabled={busy}
                    className="px-3 py-1 rounded font-semibold"
                    style={{ background: T.cyan, color: T.bg }}
                    data-testid="auto-trade-save-btn">Save</button>
          </div>
        </div>
      )}

      {/* Live log */}
      <div className="mt-3">
        <div className="text-[9px] mb-1" style={{ color: T.textDim }}>RECENT ACTIVITY</div>
        <div className="max-h-[180px] overflow-y-auto text-[10px] space-y-1">
          {logRows.length === 0 && <div style={{ color: T.textDim }}>No activity yet.</div>}
          {logRows.map((r, i) => {
            const placed = r.outcome === "placed";
            const armedEv = r.outcome === "armed";
            const disarmed = r.outcome === "disarmed";
            const rej = r.outcome === "rejected";
            const col = placed ? T.green : armedEv ? T.cyan : disarmed ? T.amber : rej ? T.red : T.textDim;
            return (
              <div key={i} className="flex gap-2"
                   style={{ borderLeft: `2px solid ${col}`, paddingLeft: 6 }}
                   data-testid={`auto-trade-log-${i}`}>
                <span style={{ color: T.textDim, minWidth: 64 }}>{(r.ts || "").slice(11, 16)}</span>
                <span style={{ color: col, fontWeight: 600, minWidth: 70 }}>{r.outcome.toUpperCase()}</span>
                <span style={{ color: T.text }}>
                  {r.symbol ? `${r.symbol} ${r.side ? r.side.toUpperCase() : ""} ${r.capital_gbp ? `£${r.capital_gbp}` : ""}` : ""}
                  <span style={{ color: T.textDim }}> · {r.reason}</span>
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </Panel>
  );
}

/* ─────────────── Approved Trades — LIVE candlestick charts ──────────────
 * Owner brief Feb 2026: "I want to see live candlestick charts on
 * approved trades with my stop loss and take profit lines drawn on".
 * Reads /api/trading/auto_log to find today's PLACED trades, then for
 * each one fetches /api/trading/symbol_bars (Capital.com 15m OHLC) and
 * renders an SVG candlestick chart with horizontal overlay lines at
 * entry, stop-loss and take-profit.
 * ────────────────────────────────────────────────────────────────── */
function CandlestickChartLive({ symbol, entry, sl, tp, side, height = 160 }) {
  const [bars, setBars] = useState(null); // null = loading, [] = empty, [bars] = data
  const [err, setErr] = useState("");
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/trading/symbol_bars?symbol=${encodeURIComponent(symbol)}&resolution=MINUTE_15&max_bars=30`);
        if (!alive) return;
        if (r.data?.error) setErr(r.data.error);
        setBars(r.data?.bars || []);
      } catch (e) {
        if (!alive) return;
        setErr(e?.message || "fetch failed");
        setBars([]);
      }
    })();
    return () => { alive = false; };
  }, [symbol]);

  if (bars === null) {
    return (
      <div className="flex items-center justify-center" style={{ height, color: T.textDim, fontSize: 11 }}>
        loading bars…
      </div>
    );
  }
  if (!bars.length) {
    return (
      <div className="flex items-center justify-center" style={{ height, color: T.textDim, fontSize: 11 }}>
        {err ? `chart unavailable · ${err}` : "no bars from Capital yet"}
      </div>
    );
  }

  // Compute price range (include overlay lines so SL/TP are always visible)
  const allLo = Math.min(...bars.map(b => b.low),  entry, sl, tp);
  const allHi = Math.max(...bars.map(b => b.high), entry, sl, tp);
  const pad = (allHi - allLo) * 0.06 || 0.0005;
  const yMin = allLo - pad;
  const yMax = allHi + pad;

  const W = 320, H = height;
  const xPad = 28, yPad = 8;
  const innerW = W - xPad - 8;
  const innerH = H - yPad * 2;
  const x = (i) => xPad + (innerW * (i + 0.5)) / bars.length;
  const y = (p) => yPad + innerH * (1 - (p - yMin) / (yMax - yMin));
  const cw = Math.max(2, (innerW / bars.length) * 0.62);

  const fmtPrice = (p) => {
    if (p >= 1000) return p.toFixed(0);
    if (p >= 10)   return p.toFixed(2);
    return p.toFixed(4);
  };

  // 5 horizontal price ticks
  const ticks = Array.from({ length: 5 }, (_, i) => yMin + ((yMax - yMin) * i) / 4);

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: "block" }}>
      {/* Gridlines */}
      {ticks.map((t, i) => (
        <g key={i}>
          <line x1={xPad} x2={W - 8} y1={y(t)} y2={y(t)} stroke="rgba(34,211,238,0.08)" strokeWidth={0.6}/>
          <text x={2} y={y(t) + 3} fill={T.textDim} fontSize="8" fontFamily="ui-monospace, monospace">
            {fmtPrice(t)}
          </text>
        </g>
      ))}

      {/* Candles */}
      {bars.map((b, i) => {
        const up = b.close >= b.open;
        const col = up ? T.green : T.red;
        const top = y(Math.max(b.open, b.close));
        const bot = y(Math.min(b.open, b.close));
        return (
          <g key={i}>
            {/* wick */}
            <line x1={x(i)} x2={x(i)} y1={y(b.high)} y2={y(b.low)} stroke={col} strokeWidth={1}/>
            {/* body */}
            <rect x={x(i) - cw / 2} y={top} width={cw} height={Math.max(1, bot - top)}
                  fill={col} stroke={col} strokeWidth={0.8}/>
          </g>
        );
      })}

      {/* Entry line (cyan, dashed) */}
      <line x1={xPad} x2={W - 8} y1={y(entry)} y2={y(entry)}
            stroke={T.cyan} strokeWidth={1.2} strokeDasharray="4 3"/>
      <rect x={W - 60} y={y(entry) - 8} width={56} height={13} fill={T.cyan} opacity={0.85}/>
      <text x={W - 56} y={y(entry) + 2} fill={T.bg} fontSize="9" fontWeight="700">
        ENTRY {fmtPrice(entry)}
      </text>

      {/* Stop-loss (red, solid) */}
      <line x1={xPad} x2={W - 8} y1={y(sl)} y2={y(sl)}
            stroke={T.red} strokeWidth={1.2}/>
      <rect x={W - 60} y={y(sl) - 8} width={56} height={13} fill={T.red} opacity={0.85}/>
      <text x={W - 56} y={y(sl) + 2} fill="#fff" fontSize="9" fontWeight="700">
        SL {fmtPrice(sl)}
      </text>

      {/* Take-profit (green, solid) */}
      <line x1={xPad} x2={W - 8} y1={y(tp)} y2={y(tp)}
            stroke={T.green} strokeWidth={1.2}/>
      <rect x={W - 60} y={y(tp) - 8} width={56} height={13} fill={T.green} opacity={0.85}/>
      <text x={W - 56} y={y(tp) + 2} fill={T.bg} fontSize="9" fontWeight="700">
        TP {fmtPrice(tp)}
      </text>

      {/* Side badge */}
      <rect x={4} y={4} width={42} height={14} fill={side === "sell" ? T.red : T.cyan} opacity={0.9}/>
      <text x={25} y={14} textAnchor="middle" fill={T.bg} fontSize="9" fontWeight="800">
        {(side || "buy").toUpperCase()}
      </text>
    </svg>
  );
}

/* ─────────────── Autonomy Panel — kill switch + pending actions ─────────
 * Owner Feb 2026: "agents that ACT, not just recommend." Green-tier
 * actions auto-apply when the toggle is ON; yellow/red always require
 * an explicit APPLY click. RED tier stays visually distinct (red badge)
 * because it moves real money.
 * ─────────────────────────────────────────────────────────────── */
function AutonomyPanel() {
  const [enabled, setEnabled] = useState(false);
  const [pending, setPending] = useState([]);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);

  const pull = async () => {
    try {
      const [rSettings, rPending, rLog] = await Promise.all([
        api.get("/autonomy/settings"),
        api.get("/autonomy/pending"),
        api.get("/autonomy/log?limit=10"),
      ]);
      setEnabled(!!rSettings.data?.autonomous_actions_enabled);
      setPending(rPending.data?.pending || []);
      setLog(rLog.data?.log || []);
    } catch { /* ignore */ }
  };
  useEffect(() => {
    pull();
    const id = setInterval(pull, 30000);  // 30s refresh — pending queue rarely long
    return () => clearInterval(id);
  }, []);

  const toggle = async () => {
    setBusy(true);
    try {
      const r = await api.post("/autonomy/settings", { autonomous_actions_enabled: !enabled });
      setEnabled(!!r.data?.autonomous_actions_enabled);
    } catch { /* ignore */ }
    finally { setBusy(false); }
  };

  const apply = async (id) => {
    setBusy(true);
    try {
      await api.post(`/autonomy/apply/${id}`);
      await pull();
    } catch (e) {
      alert(`Apply failed: ${e?.message || "unknown"}`);
    } finally { setBusy(false); }
  };

  const dismiss = async (id) => {
    setBusy(true);
    try {
      await api.post(`/autonomy/dismiss/${id}`);
      await pull();
    } catch { /* ignore */ }
    finally { setBusy(false); }
  };

  const tierColor = (t) => t === "green" ? T.green : t === "yellow" ? "#eab308" : T.red;
  const tierLabel = (t) => t === "green" ? "GREEN (safe)" : t === "yellow" ? "YELLOW (approve)" : "RED (high risk)";

  return (
    <Panel title="Autonomy · Agents That Act" testid="autonomy-panel"
           right={
             <button
               data-testid="autonomy-toggle"
               onClick={toggle}
               disabled={busy}
               className="text-[10px] font-bold uppercase tracking-wider px-3 py-1"
               style={{
                 background: enabled ? T.green : "transparent",
                 border: `1px solid ${enabled ? T.green : T.textDim}`,
                 color: enabled ? T.bg : T.textDim,
                 cursor: busy ? "not-allowed" : "pointer",
                 borderRadius: 999,
               }}
               title="When ON, green-tier actions (pause trader, widen SL, reduce stake) apply automatically. Yellow/red always require your click."
             >
               {enabled ? "✓ AUTO-APPLY ON" : "○ MANUAL ONLY"}
             </button>
           }>
      <div className="text-[10px] mb-3 leading-relaxed" style={{ color: T.textDim }}>
        Autonomous huddles produce structured actions. GREEN-tier (safe: pause trader, widen SL ≤ 10 pips, reduce stake ≤ 20%) auto-apply when the toggle is ON. YELLOW-tier (flip strategy, adjust SL/TP) and RED-tier (raise stake) always wait for your click.
      </div>

      {pending.length > 0 && (
        <div className="mb-3">
          <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: T.cyan }}>
            Pending — {pending.length} action{pending.length > 1 ? "s" : ""} waiting for you
          </div>
          <div className="space-y-2">
            {pending.map(p => (
              <div key={p.id} className="p-2"
                   data-testid={`autonomy-pending-${p.id}`}
                   style={{ border: `1px solid ${tierColor(p.action?.tier)}55`, background: "rgba(7,17,33,0.5)" }}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider"
                        style={{ color: tierColor(p.action?.tier) }}>
                    {tierLabel(p.action?.tier)} · {p.action?.type?.replace(/_/g, " ")}
                  </span>
                  <div className="flex gap-1">
                    <button
                      data-testid={`autonomy-apply-${p.id}`}
                      onClick={() => apply(p.id)}
                      disabled={busy}
                      className="text-[10px] px-2 py-0.5"
                      style={{ background: tierColor(p.action?.tier), color: T.bg, fontWeight: 700 }}
                    >APPLY</button>
                    <button
                      data-testid={`autonomy-dismiss-${p.id}`}
                      onClick={() => dismiss(p.id)}
                      disabled={busy}
                      className="text-[10px] px-2 py-0.5"
                      style={{ background: "transparent", border: `1px solid ${T.textDim}`, color: T.textDim }}
                    >Dismiss</button>
                  </div>
                </div>
                <div className="text-[10px]" style={{ color: T.text }}>
                  {p.action?.reason || "(no reason)"}
                </div>
                {p.action?.params && Object.keys(p.action.params).length > 0 && (
                  <div className="text-[9px] mt-1" style={{ color: T.textDim }}>
                    Params: {JSON.stringify(p.action.params)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {log.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: T.textDim }}>
            Recent auto-applied ({log.length})
          </div>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {log.slice(0, 6).map((l, i) => (
              <div key={l.id || i} className="text-[9px] flex items-center gap-2 py-1"
                   style={{ borderBottom: `1px solid ${T.border}` }}>
                <span style={{ color: tierColor(l.tier), fontWeight: 700 }}>{l.tier?.toUpperCase()}</span>
                <span style={{ color: T.text }}>{l.action_type?.replace(/_/g, " ")}</span>
                <span style={{ color: T.textDim, marginLeft: "auto" }}>
                  by {l.applied_by} · {(l.applied_at || "").slice(11, 16)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {pending.length === 0 && log.length === 0 && (
        <div className="text-[10px] py-2 text-center" style={{ color: T.textDim }}>
          No autonomous actions yet. The layer wakes up whenever an autonomous huddle fires (losing streak / job failure / negative expectancy).
        </div>
      )}
    </Panel>
  );
}


function AgentLeaderboard() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    let alive = true;
    const pull = async () => {
      try {
        const r = await api.get("/agents/leaderboard?limit=10");
        if (alive) setRows(r.data?.leaderboard || []);
      } catch { /* ignore */ }
    };
    pull();
    const id = setInterval(pull, 60000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const medal = (i) => i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `#${i + 1}`;
  const creditColor = (c) => c > 0 ? T.green : c < 0 ? T.red : T.textDim;

  return (
    <Panel title="Agent Leaderboard · Who Moves The Needle" testid="agent-leaderboard">
      <div className="text-[10px] mb-3 leading-relaxed" style={{ color: T.textDim }}>
        Credits track whose proposals actually improved next-5-trades P&amp;L. £1 delta = 1 credit (capped ±10/event). Settles automatically every 5 min.
      </div>
      {rows.length === 0 ? (
        <div className="text-[10px] py-2 text-center" style={{ color: T.textDim }}>
          No settled actions yet. Rankings appear here after an autonomous action settles against 5 closed trades.
        </div>
      ) : (
        <div className="space-y-1">
          {rows.map((r, i) => (
            <div key={r.agent_id} className="flex items-center gap-3 py-1.5"
                 data-testid={`leaderboard-row-${r.agent_id}`}
                 style={{ borderBottom: `1px solid ${T.border}` }}>
              <span style={{ fontSize: 14, minWidth: 28 }}>{medal(i)}</span>
              <span style={{ color: T.text, fontWeight: 600, minWidth: 60 }}>{r.name}</span>
              <span className="text-[11px] tabular-nums font-bold"
                    style={{ color: creditColor(r.credits), minWidth: 50 }}>
                {r.credits >= 0 ? "+" : ""}{r.credits}
              </span>
              <span className="text-[10px]" style={{ color: T.textDim, marginLeft: "auto" }}>
                {r.actions_influenced}× actions · {r.wins}W / {r.losses}L
                {r.win_rate_pct !== null && ` · ${r.win_rate_pct}%`}
              </span>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}


function ApprovedTradesCharts() {
  const [placed, setPlaced] = useState([]);
  // Live trade map: trade_id → {pnl, current_price} so each candlestick
  // card can show a constantly-updating "+£0.42 / -£1.20" badge. This
  // turns the page into a reward loop and helps the owner sanity-check
  // 50%-balance bets in real time without flipping to the P&L panel.
  const [liveMap, setLiveMap] = useState({});

  useEffect(() => {
    let alive = true;
    const pullLog = async () => {
      try {
        const r = await api.get("/trading/auto_log?limit=200");
        const today = new Date().toISOString().slice(0, 10);
        const todays = (r.data?.entries || []).filter(e => (e.date || "") === today && e.outcome === "placed");
        if (alive) setPlaced(todays);
      } catch { /* ignore */ }
    };
    const pullLive = async () => {
      try {
        const r = await api.get("/trades");
        const map = {};
        (r.data || []).forEach(t => {
          if (t.status === "open" && t.id) {
            map[t.id] = { pnl: t.pnl, price: t.current_price, status: "open" };
          } else if (t.id) {
            map[t.id] = { pnl: t.pnl, price: t.current_price, status: t.status };
          }
        });
        if (alive) setLiveMap(map);
      } catch { /* ignore */ }
    };
    pullLog();
    pullLive();
    const id1 = setInterval(pullLog, 30000);
    const id2 = setInterval(pullLive, 15000); // 15s — owner brief: "rewarding feedback loop"
    return () => { alive = false; clearInterval(id1); clearInterval(id2); };
  }, []);

  // Today's running total — sum of every open auto-trade P&L. Shown
  // top-right of the panel so the owner has a single glance number.
  const dayPL = placed.reduce((acc, t) => {
    const live = liveMap[t.trade_id];
    return acc + (live?.pnl || 0);
  }, 0);
  const dayPLColor = dayPL > 0 ? T.green : dayPL < 0 ? T.red : T.textDim;

  return (
    <Panel title="Approved Trades · LIVE 15m Charts" testid="approved-trades"
           right={
             <div className="flex items-center gap-2">
               <span className="text-[10px]" style={{ color: T.cyan }}>{placed.length} today</span>
               {placed.length > 0 && (
                 <span className="text-[10px] tabular-nums" style={{ color: dayPLColor, fontWeight: 700 }}
                       data-testid="approved-day-pl">
                   {dayPL >= 0 ? "+" : ""}£{dayPL.toFixed(2)}
                 </span>
               )}
             </div>
           }>
      {placed.length === 0 ? (
        <div className="text-[11px] py-4 text-center" style={{ color: T.textDim }}>
          No auto-trades placed today yet. The pattern detector scans every 60s — charts will appear here the instant a trade is opened.
        </div>
      ) : (
        <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(330px, 1fr))" }}>
          {placed.map((t, i) => {
            const entry = Number(t.price ?? t.entry ?? 0) || 0;
            // SL/TP fields are written by the auto-trader. Fall back to
            // the configured pip distances if the log row pre-dates the
            // SL/TP-on-log change.
            const sl = Number(t.stop_loss ?? t.sl ?? (t.side === "sell" ? entry + (entry * 0.0025) : entry - (entry * 0.0025))) || entry;
            const tp = Number(t.take_profit ?? t.tp ?? (t.side === "sell" ? entry - (entry * 0.0015) : entry + (entry * 0.0015))) || entry;
            const live = liveMap[t.trade_id];
            const pnl = live?.pnl;
            const pnlColor = pnl > 0 ? T.green : pnl < 0 ? T.red : T.textDim;
            const statusLabel = live?.status === "closed" ? "CLOSED" : live ? "OPEN" : "—";
            return (
              <div key={i} className="rc-panel p-2" data-testid={`approved-chart-${t.symbol}`}>
                <div className="flex items-center justify-between mb-1">
                  <span style={{ color: T.text, fontSize: 12, fontWeight: 700 }}>{(t.symbol || "?").toUpperCase()}</span>
                  <div className="flex items-center gap-2">
                    {/* Live P&L ticker — updates every 15s */}
                    {pnl !== undefined && pnl !== null && (
                      <span className="text-[11px] tabular-nums"
                            style={{ color: pnlColor, fontWeight: 800, letterSpacing: "0.04em" }}
                            data-testid={`approved-pnl-${t.symbol}`}
                            title={`Current price ${live.price ?? "?"} · ${statusLabel}`}>
                        {pnl >= 0 ? "+" : ""}£{pnl.toFixed(2)}
                      </span>
                    )}
                    <span style={{ color: T.textDim, fontSize: 10 }}>
                      {(t.ts || "").slice(11, 16)} · £{t.capital_gbp || "?"}
                    </span>
                  </div>
                </div>
                <CandlestickChartLive
                  symbol={t.symbol}
                  entry={entry}
                  sl={sl}
                  tp={tp}
                  side={t.side || "buy"}
                />
                {t.reason && (
                  <div className="text-[9px] mt-1 truncate" style={{ color: T.textDim }} title={t.reason}>
                    {t.reason}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Panel>
  );
}

/* ─────────────── Page ─────────────── */
export default function Trading() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(new Date());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [activeNav, setActiveNav] = useState("chart");

  const onNavAction = useCallback((key) => {
    setActiveNav(key);
    if (key === "settings") setSettingsOpen(true);
    else if (key === "activity") {
      // Scroll to the approved-trades panel (rightmost column)
      document.querySelector('[data-testid="approved-trades"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else if (key === "bars") {
      document.querySelector('[data-testid="sector-heatmap"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else if (key === "search") {
      // Focus the news ticker / watchlist area
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (key === "help") {
      alert("Auto-trader runs every 60s. It picks the strongest mover from your whitelist that meets the confidence threshold, then opens a position via Capital.com using your per-trade £ allocation. Use the ⚙ Settings button to change allocation, whitelist, and arm/disarm the master switch.");
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/trading/overview");
      setData(r.data || {});
    } catch (_) {
      setData({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const watchlist = data?.watchlist || [];
  const live = watchlist.some((q) => (q?.price ?? 0) > 0);
  const rows = live
    ? watchlist.map(q => ({
        symbol: q.symbol,
        price: q.price,
        change_pct: q.change_pct,
        rationale: q.change_pct >= 0 ? "AI Sector Strength" : "Sector Pullback",
      }))
    : FALLBACK_BUY;

  // One-tap-trade modal state
  const [tradeSym, setTradeSym] = useState(null);
  const [tradeSide, setTradeSide] = useState("buy");
  const openTrade = useCallback((p) => { setTradeSym(p.symbol); setTradeSide(p.side || "buy"); }, []);
  const closeTrade = useCallback(() => setTradeSym(null), []);

  return (
    <div
      className="min-h-screen w-full flex"
      style={{
        background: `radial-gradient(ellipse at top, ${T.bg2} 0%, ${T.bg} 60%)`,
        color: T.text,
        fontFamily: "ui-sans-serif, system-ui, -apple-system, sans-serif",
      }}
      data-testid="trading-page"
    >
      <LeftNav onAction={onNavAction} activeKey={activeNav}/>
      <CapitalTickerRail/>
      <TraderSettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />

      <div className="flex-1 flex flex-col min-w-0">
        <TopBar
          live={live}
          loading={loading}
          onRefresh={load}
          now={now}
          onBack={() => { window.location.href = "/"; }}
        />

        <div className="flex-1 p-3 overflow-auto">
          {/* HERO HEADER — owner brief: just the title, no marketing copy. */}
          <div className="text-center mb-4 relative" data-testid="trading-hero">
            <h1 style={{
              color: "#ffffff",
              fontSize: "clamp(28px, 4vw, 44px)",
              fontWeight: 800,
              letterSpacing: "0.5px",
              lineHeight: 1.1,
              margin: 0,
              textShadow: "0 0 14px rgba(34,211,238,0.45)",
            }}>
              RAW-DATA FILTERING MATRIX
            </h1>
            {/* 🖥️ POP-OUT to second monitor — owner brief Feb 2026: "is
                there no way of opening this raw data filter in matrix
                paid on the screen mirror please?" — opens /trading in a
                new window the owner can drag to their second screen. */}
            <button
              onClick={() => {
                const w = window.open(window.location.origin + "/trading?mirror=1", "raw-data-trading-mirror",
                  "width=1280,height=900,menubar=no,toolbar=no,location=no,status=no");
                if (w) w.focus();
              }}
              data-testid="trading-popout"
              className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1.5 rounded text-[10px] font-semibold uppercase tracking-widest transition-all"
              style={{
                background: "rgba(34,211,238,0.12)",
                border: `1px solid ${T.cyan}`,
                color: T.cyan,
                cursor: "pointer",
              }}
              title="Open on second monitor"
            >↗ POP OUT TO SCREEN MIRROR</button>
          </div>

          {/* HERO ROW: Matrix dominates (~70%) + tight right column (Buy/Trends/Sector/PortfolioRisk) */}
          <div className="grid gap-3" style={{ gridTemplateColumns: "minmax(0, 1fr) 320px" }}>
            <Panel title="Market Intelligence Engine"
                   right={<span className="text-[10px] flex items-center gap-1" style={{ color: T.cyan }}>
                            <span className="w-1.5 h-1.5 rounded-full" style={{ background: T.cyan, animation: "mi-blink 1.2s infinite" }}/>
                            analyzing live feed
                          </span>}>
              <AIFilteringMatrix rows={rows}/>
            </Panel>

            <div className="flex flex-col gap-3 min-w-0">
              <BuyOppsCompact rows={rows} onTrade={openTrade}/>
              <MarketTrendsTop/>
              <SectorHeatmap/>
              <PortfolioRiskTop/>
            </div>
          </div>

          {/* AUTO-TRADER control panel */}
          <div className="mt-3"><AutoTradePanel/></div>

          {/* AUTONOMY LAYER — kill switch + pending action queue */}
          <div className="mt-3"><AutonomyPanel/></div>

          {/* AGENT LEADERBOARD — who moves the needle */}
          <div className="mt-3"><AgentLeaderboard/></div>

          {/* APPROVED TRADES — live 15m candlestick charts with SL/TP overlay */}
          <div className="mt-3"><ApprovedTradesCharts/></div>

          {/* BOTTOM ROW: 4 equal cards */}
          <div className="grid gap-3 mt-3" style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))" }}>
            <BuyOppsDetail rows={rows} onTrade={openTrade}/>
            <MarketTrendsBottom/>
            <RiskScoresPanel/>
            <SmartWatchlistsPanel/>
          </div>

          {!live && (
            <div className="mt-3 rounded text-[10px] px-3 py-2"
                 style={{ background: "rgba(245,158,11,0.08)", border: `1px solid ${T.amber}`, color: T.textDim }}
                 data-testid="trading-data-note">
              <span style={{ color: T.amber, fontWeight: 600 }}>DATA NOTE: </span>
              Alpha Vantage free tier (25 req/day) exhausted — showing mockup data. Upgrade your AV key (or wait for UTC midnight reset) and the LIVE badge will flip automatically.
              {data?.cached_at && <span className="ml-2 opacity-60">· last fetch: {data.cached_at}</span>}
            </div>
          )}
        </div>

        <BottomBar/>
      </div>

      <TradeModal
        open={!!tradeSym}
        symbol={tradeSym}
        suggested={{ side: tradeSide, capital: 100 }}
        onClose={closeTrade}
        onPlaced={() => { /* keep modal open with success screen */ }}
      />
    </div>
  );
}
